<template>
  <div class="project-pattern-structure">
    <div class="project-pattern-structure__header">
      <div class="header-controls">
        <label class="header-label">Проект УСП:</label>
        
        <q-input
          v-model="selectedProjectName"
          outlined
          dense
          readonly
          placeholder="Выберите проект"
          class="guid-input"
          style="width: 400px;"
        >
          <template v-slot:prepend>
            <q-btn
              icon="list"
              flat
              dense
              round
              @click="openProjectSelector"
            >
              <q-tooltip>Выбрать проект</q-tooltip>
            </q-btn>
          </template>
          <template v-slot:append>
            <q-btn
              v-if="projectGuid"
              icon="clear"
              flat
              dense
              round
              @click="clearUspProject"
            >
              <q-tooltip>Очистить выбор</q-tooltip>
            </q-btn>
            <q-btn
              v-else
              icon="search"
              flat
              dense
              round
              @click="loadData"
              :loading="isLoading"
              :disable="!projectGuid"
            >
              <q-tooltip>Загрузить структуру</q-tooltip>
            </q-btn>
          </template>
        </q-input>
        
        
        <q-btn
          icon="refresh"
          unelevated
          dense
          round
          size="md"
          color="grey-7"
          @click="loadDataSilently"
          :loading="isSilentLoading"
          :disable="!activeProjectGuid"
        >
          <q-tooltip>Обновить данные</q-tooltip>
        </q-btn>

        <q-btn
          label="Связать"
          icon="link"
          unelevated
          dense
          no-caps
          size="md"
          color="primary"
          @click="onManualLinkClick"
          :disable="!canManualLink"
        >
          <q-tooltip>Связать выбранный элемент слева с выбранным элементом справа</q-tooltip>
        </q-btn>

        <q-btn
          label="Проверка отправки"
          icon="fact_check"
          unelevated
          dense
          no-caps
          size="md"
          color="deep-orange"
          @click="runCheckSendReadiness"
          :loading="isCheckingSend"
          :disable="!activeProjectGuid"
        >
          <q-tooltip>Проверить готовность каждого этажа к отправке оперфакта в УСП</q-tooltip>
        </q-btn>
      </div>
    </div>

    <div class="project-pattern-structure__content">
      <q-splitter
        v-model="splitterModel"
        style="height: calc(100vh - 140px);"
      >
        <!-- Левая панель: Проекты Master -->
        <template v-slot:before>
          <div class="panel-container">
            <div class="panel-header">
              <h6 class="panel-title">Проекты Master</h6>
            </div>
            
            <q-linear-progress v-if="isLoadingProjects" indeterminate />
            
            <div v-if="!isLoadingProjects && projectTreeData.length > 0" class="tree-container" @click="clearNodeSelection">
              <div class="tree-controls">
                <q-btn
                  label="Развернуть все"
                  icon="unfold_more"
                  flat
                  dense
                  size="sm"
                  color="primary"
                  @click="expandAllProjects"
                />
                <q-btn
                  label="Свернуть все"
                  icon="unfold_less"
                  flat
                  dense
                  size="sm"
                  color="primary"
                  @click="collapseAllProjects"
                />
                <q-separator vertical inset />
                <q-btn
                  :label="showOnlyUnlinkedLeft ? 'Все' : 'Несвязанные'"
                  :icon="showOnlyUnlinkedLeft ? 'link' : 'link_off'"
                  flat
                  dense
                  size="sm"
                  :color="showOnlyUnlinkedLeft ? 'orange' : 'grey'"
                  @click="showOnlyUnlinkedLeft = !showOnlyUnlinkedLeft"
                >
                  <q-tooltip>{{ showOnlyUnlinkedLeft ? 'Показать все' : 'Показать только несвязанные' }}</q-tooltip>
                </q-btn>
              </div>
              
              <q-tree
                :nodes="filteredProjectTreeData"
                node-key="id"
                :expanded.sync="expandedProjects"
                no-nodes-label="Не найдено"
                no-results-label="Не найдено"
                ref="projectTree"
              >
                <template v-slot:default-header="prop">
                  <div 
                    class="row items-center tree-node" 
                    :class="{ 'tree-node--selected-left': selectedLeftNode && selectedLeftNode.id === prop.node.id }"
                    style="width: 100%; gap: 10px; padding: 4px 8px;"
                    :data-node-id="prop.node.id"
                    @click.stop
                  >
                    <q-btn
                      v-if="prop.node.children && prop.node.children.length > 0"
                      :icon="expandedProjectsSet.has(prop.node.id) ? 'remove' : 'add'"
                      flat
                      dense
                      round
                      size="xs"
                      @click.stop="toggleProjectNode(prop.node.id)"
                      class="expand-btn"
                    />
                    <span v-else style="width: 24px;"></span>
                    
                    <div style="color: #000; flex: 1;">
                      <div style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
                        <span>{{ prop.node.label }}</span>
                        <span v-if="prop.node.guid" style="font-size: 11px; color: #1976d2; font-family: monospace; font-weight: 500;">
                          [guid: {{ prop.node.guid }}]
                        </span>
                        <span v-if="prop.node.resident_id" style="font-size: 11px; color: #999; font-family: monospace;">
                          [resident_id: {{ prop.node.resident_id }}]
                        </span>
                        <span v-if="prop.node.project_type_id" style="font-size: 11px; color: #999; font-family: monospace;">
                          [project_type_id: {{ prop.node.project_type_id }}]
                        </span>
                        <span v-if="prop.node.project_id" style="font-size: 11px; color: #999; font-family: monospace;">
                          [project_id: {{ prop.node.project_id }}]
                        </span>
                        <span v-if="prop.node.project_entrance_id" style="font-size: 11px; color: #999; font-family: monospace;">
                          [project_entrance_id: {{ prop.node.project_entrance_id }}]
                        </span>
                        <span v-if="prop.node.floor_map_id" style="font-size: 11px; color: #999; font-family: monospace;">
                          [floor_map_id: {{ prop.node.floor_map_id }}]
                        </span>
                      </div>
                      <template v-if="isProjectTypeNode(prop.node)">
                        <div
                          v-for="sectionSettings in [getProjectTypeSectionSettings(prop.node)]"
                          :key="'pts-' + prop.node.id"
                          class="project-type-map-info"
                        >
                          <template v-if="!sectionSettings.length">
                            <q-badge color="blue-grey" text-color="white" class="project-type-map-badge" label="Не настроен">
                              <q-tooltip>Для этого типа проекта не настроен ожидаемый раздел</q-tooltip>
                            </q-badge>
                          </template>
                          <template v-else-if="sectionSettings.length === 1">
                            <q-icon name="layers" size="13px" color="indigo" />
                            <span class="project-type-map-text">
                              {{ sectionSettings[0].section_name }}
                              <template v-if="sectionSettings[0].required_node_name">
                                <span class="project-type-map-node">
                                  / {{ sectionSettings[0].required_node_name }}
                                </span>
                              </template>
                            </span>
                          </template>
                          <template v-else>
                            <q-badge color="indigo" text-color="white" class="project-type-map-badge">
                              <q-icon name="layers" size="12px" class="q-mr-xs" />
                              {{ sectionSettings.length }} раздела
                            </q-badge>
                            <span class="project-type-map-text">
                              {{ sectionSettings[0].section_name }}
                              и ещё {{ sectionSettings.length - 1 }}
                            </span>
                            <q-tooltip>
                              <div
                                v-for="line in getProjectTypeSectionTooltip(prop.node)"
                                :key="line"
                                class="project-type-map-tooltip-line"
                              >{{ line }}</div>
                            </q-tooltip>
                          </template>
                        </div>
                      </template>
                      <div
                        v-if="isLinked(prop.node)"
                        style="display: flex; align-items: center; gap: 4px; margin-top: 4px;"
                      >
                        <q-badge
                          :color="prop.node.project_entrance_id && !prop.node.floor_map_id ? 'indigo-6' : 'green'"
                          label="связан"
                          class="linked-badge"
                          @click.stop="showLinkPath(prop.node)"
                        >
                          <q-tooltip>Показать путь связи</q-tooltip>
                        </q-badge>
                        <q-icon name="arrow_forward" size="14px" color="grey-6" />
                        <span style="font-size: 12px; color: #666; font-style: italic;">
                          {{ getLinkedNodeName(prop.node) }}
                        </span>
                      </div>
                      <div
                        v-else-if="prop.node.project_entrance_id || prop.node.floor_map_id"
                        style="display: flex; align-items: center; gap: 6px; margin-top: 4px;"
                      >
                        <q-badge
                          color="grey-6"
                          text-color="white"
                          label="не связан"
                        />
                        <q-icon name="link_off" size="14px" color="grey-6" />
                        <span style="font-size: 12px; color: #999; font-style: italic;">
                          нет связи с УСП
                        </span>
                      </div>
                      <!-- Статистика этажей для подъезда -->
                      <div v-if="prop.node.project_entrance_id && prop.node.children" style="margin-top: 4px;">
                        <q-badge
                          :color="getFloorStatsColor(prop.node)"
                          :label="getFloorStats(prop.node)"
                        />
                      </div>
                      <!-- Статус отправки в УСП (только для блоков, не для этажей) -->
                      <div v-if="prop.node.project_entrance_id && !prop.node.floor_map_id" style="display: flex; align-items: center; gap: 8px; margin-top: 4px;">
                        <!-- Смета -->
                        <div 
                          :style="{
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px', 
                            cursor: 'pointer', 
                            padding: '2px 6px', 
                            background: prop.node.usp_estimate_error ? '#ffebee' : (prop.node.is_send_usp_estimate ? '#e8f5e9' : '#f5f5f5'), 
                            borderRadius: '4px'
                          }"
                          @click.stop="openEstimateDialog(prop.node)"
                        >
                          <q-icon 
                            :name="prop.node.usp_estimate_error ? 'error' : (prop.node.is_send_usp_estimate ? 'check_circle' : 'cancel')" 
                            :color="prop.node.usp_estimate_error ? 'red' : (prop.node.is_send_usp_estimate ? 'green' : 'grey')" 
                            size="16px"
                          />
                          <span :style="{ fontSize: '11px', color: prop.node.usp_estimate_error ? '#d32f2f' : (prop.node.is_send_usp_estimate ? '#2e7d32' : '#666') }">Смета</span>
                          <q-tooltip>{{ prop.node.usp_estimate_error ? 'Ошибка' : (prop.node.is_send_usp_estimate ? 'Отправлена' : 'Не отправлена') }}</q-tooltip>
                        </div>
                        <!-- Договор -->
                        <div 
                          :style="{
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px', 
                            cursor: 'pointer', 
                            padding: '2px 6px', 
                            background: prop.node.usp_contract_error ? '#ffebee' : (prop.node.is_send_usp_contract ? '#e8f5e9' : '#f5f5f5'), 
                            borderRadius: '4px'
                          }"
                          @click.stop="openContractDialog(prop.node)"
                        >
                          <q-icon 
                            :name="prop.node.usp_contract_error ? 'error' : (prop.node.is_send_usp_contract ? 'check_circle' : 'cancel')" 
                            :color="prop.node.usp_contract_error ? 'red' : (prop.node.is_send_usp_contract ? 'green' : 'grey')" 
                            size="16px"
                          />
                          <span :style="{ fontSize: '11px', color: prop.node.usp_contract_error ? '#d32f2f' : (prop.node.is_send_usp_contract ? '#2e7d32' : '#666') }">Договор</span>
                          <q-tooltip>{{ prop.node.usp_contract_error ? 'Ошибка' : (prop.node.is_send_usp_contract ? 'Отправлен' : 'Не отправлен') }}</q-tooltip>
                        </div>
                        <!-- Оперфакт -->
                        <div 
                          :style="{
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: '4px', 
                            cursor: 'pointer', 
                            marginLeft: '8px', 
                            padding: '2px 6px', 
                            background: prop.node.has_operfact_error ? '#ffebee' : (prop.node.has_operfact_pending ? '#fff3e0' : (prop.node.has_operfact ? '#e8f5e9' : '#f5f5f5')), 
                            borderRadius: '4px'
                          }"
                          @click.stop="openOperfactDialog(prop.node)"
                        >
                          <q-icon 
                            name="send" 
                            size="14px" 
                            :color="prop.node.has_operfact_error ? 'red' : (prop.node.has_operfact_pending ? 'orange' : (prop.node.has_operfact ? 'green' : 'grey'))" 
                          />
                          <span :style="{ fontSize: '11px', color: prop.node.has_operfact_error ? '#d32f2f' : (prop.node.has_operfact_pending ? '#e65100' : (prop.node.has_operfact ? '#2e7d32' : '#666')) }">Оперфакт</span>
                          <q-tooltip>
                            {{ prop.node.has_operfact_error ? 'Есть ошибки отправки' : (prop.node.has_operfact_pending ? 'Есть неотправленные' : (prop.node.has_operfact ? 'Все отправлено' : 'Нет данных')) }}
                          </q-tooltip>
                        </div>
                      </div>
                      <!-- Оперфакт для этажей -->
                      <div v-if="prop.node.floor_map_id" style="margin-top: 4px;">
                        <div 
                          :style="{
                            display: 'inline-flex', 
                            alignItems: 'center', 
                            gap: '4px', 
                            cursor: 'pointer', 
                            padding: '2px 6px', 
                            background: prop.node.has_operfact_error ? '#ffebee' : (prop.node.has_operfact_pending ? '#fff3e0' : (prop.node.has_operfact ? '#e8f5e9' : '#f5f5f5')), 
                            borderRadius: '4px'
                          }"
                          @click.stop="openOperfactDialog(prop.node)"
                        >
                          <q-icon 
                            name="send" 
                            size="14px" 
                            :color="prop.node.has_operfact_error ? 'red' : (prop.node.has_operfact_pending ? 'orange' : (prop.node.has_operfact ? 'green' : 'grey'))" 
                          />
                          <span :style="{ fontSize: '11px', color: prop.node.has_operfact_error ? '#d32f2f' : (prop.node.has_operfact_pending ? '#e65100' : (prop.node.has_operfact ? '#2e7d32' : '#666')) }">Оперфакт</span>
                          <q-tooltip>
                            {{ prop.node.has_operfact_error ? 'Есть ошибки отправки' : (prop.node.has_operfact_pending ? 'Есть неотправленные' : (prop.node.has_operfact ? 'Все отправлено' : 'Нет данных')) }}
                          </q-tooltip>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Кнопка фильтрации этажей (для подъезда) -->
                    <q-btn
                      v-if="prop.node.project_entrance_id && prop.node.usp_block_guid"
                      :icon="selectedEntranceForFloors?.project_entrance_id === prop.node.project_entrance_id ? 'filter_alt_off' : 'filter_alt'"
                      flat
                      dense
                      round
                      size="sm"
                      :color="selectedEntranceForFloors?.project_entrance_id === prop.node.project_entrance_id ? 'orange' : 'primary'"
                      @click.stop="toggleEntranceFilter(prop.node)"
                    >
                      <q-tooltip>{{ selectedEntranceForFloors?.project_entrance_id === prop.node.project_entrance_id ? 'Сбросить фильтр' : 'Фильтровать этажи' }}</q-tooltip>
                    </q-btn>
                    
                    <!-- Кнопка удаления связи -->
                    <q-btn
                      v-if="isLinked(prop.node)"
                      icon="link_off"
                      flat
                      dense
                      round
                      size="sm"
                      color="negative"
                      @click.stop="removeLink(prop.node)"
                    >
                      <q-tooltip>Удалить связь</q-tooltip>
                    </q-btn>

                    <!-- Кнопка выбора узла слева для связи -->
                    <q-btn
                      v-if="isDraggableEntrance(prop.node)"
                      :icon="selectedLeftNode && selectedLeftNode.id === prop.node.id ? 'radio_button_checked' : 'radio_button_unchecked'"
                      flat
                      dense
                      round
                      size="sm"
                      :color="selectedLeftNode && selectedLeftNode.id === prop.node.id ? 'primary' : 'grey'"
                      @click.stop="onLeftNodeClick(prop.node)"
                    >
                      <q-tooltip>Выбрать слева для связи</q-tooltip>
                    </q-btn>
                  </div>
                </template>
              </q-tree>
            </div>
            
            <div v-else-if="!isLoadingProjects && projectTreeData.length === 0 && activeProjectGuid" class="empty-state">
              <q-icon name="folder_off" color="grey-5" size="36px" />
              <p>Нет данных</p>
            </div>
            
            <div v-else-if="!isLoadingProjects && !activeProjectGuid" class="empty-state">
              <q-icon name="info" color="grey-5" size="36px" />
              <p>Выберите проект УСП</p>
            </div>
          </div>
        </template>

        <!-- Правая панель: Структура УСП (SBS) -->
        <template v-slot:after>
          <div class="panel-container">
            <div class="panel-header">
              <div style="display: flex; align-items: center; justify-content: space-between;">
                <h6 class="panel-title">Структура УСП (SBS)</h6>
                <q-btn
                  v-if="activeProjectGuid"
                  label="Обновить структуру"
                  icon="refresh"
                  color="primary"
                  size="sm"
                  unelevated
                  @click="refreshProjectPattern"
                  :loading="isRefreshingPattern"
                >
                  <q-tooltip>Обновить структуру проекта УСП из источника</q-tooltip>
                </q-btn>
              </div>
              <div v-if="activeProjectGuid" style="display: flex; align-items: center; gap: 6px; padding: 8px 12px; background: #e3f2fd; border-radius: 4px; margin-top: 8px;">
                <q-icon name="info" color="primary" size="16px" />
                <span style="font-size: 12px; color: #1565c0;">
                  Если структура была изменена на стороне УСП, нажмите "Обновить структуру" для загрузки актуальных данных.
                </span>
              </div>
            </div>
            
            <q-linear-progress v-if="isLoading" indeterminate />
            
            <div v-if="!isLoading && treeData.length > 0" class="tree-container" @click="clearNodeSelection">
              <div class="tree-controls">
                <q-btn
                  label="Развернуть все"
                  icon="unfold_more"
                  flat
                  dense
                  size="sm"
                  color="primary"
                  @click="expandAll"
                />
                <q-btn
                  label="Свернуть все"
                  icon="unfold_less"
                  flat
                  dense
                  size="sm"
                  color="primary"
                  @click="collapseAll"
                />
                <q-separator vertical inset />
                <q-btn
                  :label="showOnlyUnlinkedRight ? 'Все' : 'Несвязанные'"
                  :icon="showOnlyUnlinkedRight ? 'link' : 'link_off'"
                  flat
                  dense
                  size="sm"
                  :color="showOnlyUnlinkedRight ? 'orange' : 'grey'"
                  @click="showOnlyUnlinkedRight = !showOnlyUnlinkedRight"
                >
                  <q-tooltip>{{ showOnlyUnlinkedRight ? 'Показать все' : 'Показать только несвязанные' }}</q-tooltip>
                </q-btn>
              </div>
              
              <!-- Индикатор фильтрации -->
              <div v-if="selectedEntranceForFloors" style="padding: 8px; background-color: #fff3e0; border-radius: 4px; margin-bottom: 12px;">
                <div style="display: flex; align-items: center; gap: 8px;">
                  <q-icon name="filter_alt" color="orange" size="sm" />
                  <span style="font-size: 13px; color: #e65100; flex: 1;">
                    Фильтр: {{ selectedEntranceForFloors.label }}
                  </span>
                  <q-btn
                    icon="close"
                    flat
                    dense
                    round
                    size="xs"
                    color="orange"
                    @click="clearEntranceFilter"
                  >
                    <q-tooltip>Сбросить фильтр</q-tooltip>
                  </q-btn>
                </div>
              </div>
              
              <q-tree
                :nodes="selectedEntranceForFloors ? filteredTreeData : filteredUspTreeData"
                node-key="id"
                :expanded.sync="expanded"
                no-nodes-label="Не найдено"
                no-results-label="Не найдено"
                ref="tree"
              >
                <template v-slot:default-header="prop">
                  <div 
                    class="row items-center tree-node" 
                    :class="{ 'tree-node--selected-right': selectedRightNode && selectedRightNode.id === prop.node.id }"
                    style="width: 100%; gap: 10px; padding: 4px 8px;"
                    :data-node-id="prop.node.id"
                    @click.stop
                  >
                    <q-btn
                      v-if="prop.node.children && prop.node.children.length > 0"
                      :icon="expandedSet.has(prop.node.id) ? 'remove' : 'add'"
                      flat
                      dense
                      round
                      size="xs"
                      @click.stop="toggleNode(prop.node.id)"
                      class="expand-btn"
                    />
                    <span v-else style="width: 24px;"></span>
                    
                    <div
                      :class="{'text-weight-bold': prop.node.isBold}"
                      style="color: #000; flex: 1;"
                    >
                      <div style="display: flex; align-items: center; gap: 6px; flex-wrap: wrap;">
                        <span>{{ prop.node.label }}</span>
                        <!-- Иконка warning если у узла есть дети, но нет работы -->
                        <q-icon
                          v-if="prop.node.missingWork"
                          name="warning"
                          color="orange"
                          size="18px"
                          class="cursor-pointer"
                          @click.stop="showMissingWorkDialog(prop.node)"
                        >
                          <q-tooltip>Нет работы в этом узле. Нажмите для подробностей.</q-tooltip>
                        </q-icon>
                        <!-- Badge с количеством проблем в поддереве -->
                        <q-badge
                          v-if="!prop.node.missingWork && prop.node.missingWorkCount > 0"
                          color="orange"
                          text-color="white"
                          class="cursor-pointer"
                          @click.stop="showMissingWorkDialog(prop.node)"
                        >
                          {{ prop.node.missingWorkCount }}
                          <q-tooltip>Узлов без работ: {{ prop.node.missingWorkCount }}. Нажмите для подробностей.</q-tooltip>
                        </q-badge>
                        <span v-if="prop.node.guid" style="font-size: 11px; color: #1976d2; font-family: monospace; font-weight: 500;">
                          [guid: {{ prop.node.guid }}]
                        </span>
                        <q-badge
                          v-if="hasLinkedEntrances(prop.node)"
                          color="blue"
                          :label="getLinkedNodesBadgeLabel(prop.node)"
                          class="q-ml-xs linked-badge"
                          @click.stop="showLinkedItems(prop.node)"
                        />
                      </div>
                    </div>

                    <!-- Кнопка выбора узла справа для связи -->
                    <q-btn
                      v-if="prop.node.guid && !prop.node.project_entrance_id && !prop.node.floor_map_id"
                      :icon="selectedRightNode && selectedRightNode.id === prop.node.id ? 'radio_button_checked' : 'radio_button_unchecked'"
                      flat
                      dense
                      round
                      size="sm"
                      :color="selectedRightNode && selectedRightNode.id === prop.node.id ? 'primary' : 'grey'"
                      @click.stop="onRightNodeClick(prop.node)"
                    >
                      <q-tooltip>Выбрать справа для связи</q-tooltip>
                    </q-btn>
                  </div>
                </template>
              </q-tree>
            </div>
            
            <div v-else-if="!isLoading && treeData.length === 0 && activeProjectGuid" class="empty-state">
              <q-icon name="folder_off" color="grey-5" size="36px" />
              <p>Нет данных</p>
            </div>
            
            <div v-else-if="!isLoading && !activeProjectGuid" class="empty-state">
              <q-icon name="info" color="grey-5" size="36px" />
              <p>Выберите проект УСП</p>
            </div>
          </div>
        </template>
      </q-splitter>
    </div>

    <!-- Диалог подтверждения удаления связи -->
    <q-dialog v-model="confirmDialog.show" persistent>
      <q-card style="min-width: 350px">
        <q-card-section>
          <div class="text-h6">{{ confirmDialog.title }}</div>
        </q-card-section>

        <q-card-section>
          {{ confirmDialog.message }}
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="confirmDialog.show = false" />
          <q-btn unelevated label="Удалить" color="negative" @click="onConfirmDelete" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог со списком привязанных элементов -->
    <q-dialog v-model="linkedItemsDialog.show">
      <q-card style="min-width: 700px; max-width: 900px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Привязанные элементы</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <div style="margin-bottom: 16px;">
            <div style="display: flex; align-items: center; gap: 8px;">
              <q-icon name="account_tree" color="primary" size="sm" />
              <strong>Узел УСП:</strong>
              <span style="color: #666;">{{ linkedItemsDialog.nodeName }}</span>
            </div>
          </div>
          
          <q-separator spaced />
          
          <div v-if="linkedItemsDialog.items.length === 0" style="text-align: center; padding: 20px; color: #999;">
            Нет привязанных элементов
          </div>

          <div v-else style="display: flex; flex-direction: column; gap: 16px; margin-top: 16px;">
            <div 
              v-for="item in linkedItemsDialog.items" 
              :key="item.id"
              class="linked-item-card"
            >
              <div style="display: flex; align-items: flex-start; gap: 12px; padding: 16px; background-color: #f9f9f9; border-radius: 8px; border: 1px solid #e0e0e0;">
                <q-icon 
                  :name="item.type === 'entrance' ? 'apartment' : 'layers'" 
                  :color="item.type === 'entrance' ? 'primary' : 'orange'"
                  size="md"
                  style="margin-top: 4px;"
                />
                
                <div style="flex: 1;">
                  <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
                    <span style="font-weight: 600; font-size: 15px;">{{ item.label }}</span>
                    <q-badge 
                      :color="item.type === 'entrance' ? 'primary' : 'orange'"
                      :label="item.type === 'entrance' ? 'Подъезд' : 'Этаж'"
                    />
                  </div>
                  
                  <!-- Путь в дереве -->
                  <div v-if="item.path && item.path.length > 0" style="margin-top: 12px; padding-left: 8px; border-left: 3px solid #e0e0e0;">
                    <div style="font-size: 12px; color: #666; margin-bottom: 8px; font-weight: 500;">
                      Путь в дереве проектов:
                    </div>
                    <div style="display: flex; flex-direction: column; gap: 4px;">
                      <div 
                        v-for="(pathNode, index) in item.path" 
                        :key="pathNode.id"
                        style="display: flex; align-items: center; gap: 6px;"
                      >
                        <div 
                          :style="{
                            width: (index * 16) + 'px',
                            height: '1px'
                          }"
                        ></div>
                        
                        <q-icon 
                          :name="index === item.path.length - 1 ? 'radio_button_checked' : 'arrow_forward'" 
                          :color="index === item.path.length - 1 ? 'green' : 'grey-6'" 
                          size="xs"
                        />
                        
                        <span 
                          :style="{
                            fontSize: '12px',
                            fontWeight: index === item.path.length - 1 ? '600' : 'normal',
                            color: index === item.path.length - 1 ? '#2e7d32' : '#555'
                          }"
                        >
                          {{ pathNode.label }}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <q-btn
                  icon="link_off"
                  flat
                  dense
                  round
                  size="sm"
                  color="negative"
                  @click="removeLinkedItem(item)"
                  style="margin-top: 4px;"
                >
                  <q-tooltip>Удалить связь</q-tooltip>
                </q-btn>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Закрыть" color="primary" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог подтверждения создания связи -->
    <q-dialog v-model="linkConfirmDialog.show" persistent>
      <q-card style="min-width: 480px; max-width: 640px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Создание связи</div>
          <q-space />
          <q-btn icon="close" flat round dense @click="linkConfirmDialog.show = false" />
        </q-card-section>

        <q-card-section>
          <!-- Левый узел -->
          <div style="border: 1px solid #e0e0e0; border-radius: 8px; padding: 12px 16px; background: #f9f9f9;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
              <q-badge
                :color="linkConfirmDialog.leftNode && linkConfirmDialog.leftNode.floor_map_id ? 'green-7' : 'indigo-6'"
                :label="linkConfirmDialog.leftNode && linkConfirmDialog.leftNode.floor_map_id ? 'Этаж' : 'Блок'"
              />
              <span style="font-weight: 600; font-size: 15px;">{{ linkConfirmDialog.leftNode && linkConfirmDialog.leftNode.label }}</span>
            </div>
            <div v-if="linkConfirmDialog.leftPath.length > 1" style="font-size: 12px; color: #888; display: flex; align-items: center; gap: 4px; flex-wrap: wrap;">
              <template v-for="(n, i) in linkConfirmDialog.leftPath.slice(0, -1)" :key="i">
                <span>{{ n.label }}</span>
                <q-icon name="chevron_right" size="14px" />
              </template>
            </div>
          </div>

          <!-- Стрелка -->
          <div style="text-align: center; padding: 10px 0; color: #1976d2;">
            <q-icon name="link" size="28px" />
            <div style="font-size: 11px; color: #999; margin-top: 2px;">будет связан с</div>
          </div>

          <!-- Правый узел (УСП) -->
          <div style="border: 1px solid #c8d8f0; border-radius: 8px; padding: 12px 16px; background: #f0f5ff;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 6px;">
              <q-badge color="blue-8" label="УСП" />
              <span style="font-weight: 600; font-size: 15px;">{{ linkConfirmDialog.rightNode && linkConfirmDialog.rightNode.label }}</span>
            </div>
            <div v-if="linkConfirmDialog.rightPath.length > 1" style="font-size: 12px; color: #888; display: flex; align-items: center; gap: 4px; flex-wrap: wrap;">
              <template v-for="(n, i) in linkConfirmDialog.rightPath.slice(0, -1)" :key="i">
                <span>{{ n.label }}</span>
                <q-icon name="chevron_right" size="14px" />
              </template>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="linkConfirmDialog.show = false" />
          <q-btn unelevated label="Связать" icon="link" color="primary" @click="onLinkConfirm" />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог выбора проекта УСП -->
    <q-dialog v-model="projectSelectorDialog.show" persistent>
      <q-card style="min-width: 800px; max-width: 1000px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Выбор проекта УСП</div>
          <q-space />
          <q-btn
            label="Обновить список проектов"
            icon="sync"
            color="primary"
            flat
            dense
            :loading="isIngestingProjects"
            @click="onIngestProjectGroups"
            class="q-mr-sm"
          >
            <q-tooltip>Загрузить актуальные проекты из УСП</q-tooltip>
          </q-btn>
          <q-btn icon="close" flat round dense @click="projectSelectorDialog.show = false" />
        </q-card-section>

        <q-card-section>
          <!-- Поиск -->
          <q-input
            v-model="projectSearchQuery"
            outlined
            dense
            placeholder="Поиск по названию проекта или типу..."
            clearable
            autofocus
          >
            <template v-slot:prepend>
              <q-icon name="search" />
            </template>
          </q-input>

          <!-- Хинт -->
          <div style="display: flex; align-items: center; gap: 8px; margin-top: 12px; padding: 8px 12px; background-color: #e3f2fd; border-radius: 4px; font-size: 13px; color: #1565c0;">
            <q-icon name="info" size="18px" />
            <span>Не нашли нужный проект? Нажмите "Обновить список проектов" для загрузки актуальных данных из УСП.</span>
          </div>

          <!-- Таблица проектов -->
          <div style="margin-top: 16px;">
            <q-linear-progress v-if="isLoadingProjects" indeterminate />
            
            <div v-else-if="filteredProjects.length === 0" style="text-align: center; padding: 40px; color: #999;">
              <q-icon name="folder_off" size="48px" />
              <div style="margin-top: 12px;">Проекты не найдены</div>
            </div>

            <q-table
              v-else
              :rows="filteredProjects"
              :columns="projectColumns"
              row-key="project_group_id"
              flat
              bordered
              :rows-per-page-options="[10, 20, 50]"
              :pagination="{ rowsPerPage: 10 }"
              style="max-height: 500px;"
              virtual-scroll
            >
              <template v-slot:body-cell-actions="props">
                <q-td :props="props">
                  <q-btn
                    label="Выбрать"
                    color="primary"
                    size="sm"
                    unelevated
                    @click="selectProject(props.row)"
                  />
                </q-td>
              </template>
              
              <template v-slot:body-cell-project_guid="props">
                <q-td :props="props">
                  <div style="font-family: monospace; font-size: 11px; color: #666;">
                    {{ props.value }}
                  </div>
                </q-td>
              </template>
            </q-table>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Отмена" color="grey" @click="projectSelectorDialog.show = false" />
        </q-card-actions>
      </q-card>
    </q-dialog>


    <!-- Диалог показа пути связи -->
    <q-dialog v-model="linkPathDialog.show">
      <q-card style="min-width: 600px; max-width: 800px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Путь связи</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <div style="margin-bottom: 16px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 8px;">
              <q-icon name="account_tree" color="primary" size="sm" />
              <strong>Источник:</strong>
              <span style="color: #666;">{{ linkPathDialog.sourceName }}</span>
            </div>
            <div style="display: flex; align-items: center; gap: 8px;">
              <q-icon name="link" color="green" size="sm" />
              <strong>Связан с:</strong>
              <span style="color: #666;">{{ linkPathDialog.targetName }}</span>
            </div>
          </div>

          <q-separator spaced />

          <div style="padding: 16px; background-color: #f5f5f5; border-radius: 8px;">
            <div style="font-size: 13px; font-weight: 500; margin-bottom: 12px; color: #555;">
              Путь в дереве УСП:
            </div>
            
            <div v-if="linkPathDialog.path.length === 0" style="text-align: center; padding: 20px; color: #999;">
              Путь не найден
            </div>

            <div v-else style="display: flex; flex-direction: column; gap: 8px;">
              <div 
                v-for="(node, index) in linkPathDialog.path" 
                :key="node.id"
                style="display: flex; align-items: center; gap: 8px;"
              >
                <div style="display: flex; align-items: center; gap: 8px; flex: 1;">
                  <div 
                    :style="{
                      width: (index * 20) + 'px',
                      height: '1px',
                      backgroundColor: '#ccc'
                    }"
                  ></div>
                  
                  <q-icon 
                    :name="index === linkPathDialog.path.length - 1 ? 'radio_button_checked' : 'folder'" 
                    :color="index === linkPathDialog.path.length - 1 ? 'green' : 'primary'" 
                    size="sm"
                  />
                  
                  <div style="flex: 1;">
                    <div :style="{
                      fontWeight: index === linkPathDialog.path.length - 1 ? 'bold' : 'normal',
                      color: index === linkPathDialog.path.length - 1 ? '#2e7d32' : '#333'
                    }">
                      {{ node.label }}
                    </div>
                    <div style="font-size: 11px; color: #1976d2; font-family: monospace; margin-top: 2px;">
                      guid: {{ node.guid }}
                    </div>
                  </div>
                  
                  <q-badge 
                    v-if="index === linkPathDialog.path.length - 1"
                    color="green"
                    label="связан здесь"
                  />
                </div>
              </div>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Закрыть" color="primary" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог списка веток без работ -->
    <q-dialog v-model="noWorkDialog.show">
      <q-card style="min-width: 600px; max-width: 900px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            <q-icon name="warning" color="orange" class="q-mr-sm" />
            Узлы без работ
          </div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <!-- Статистика -->
          <div style="display: flex; gap: 20px; margin-bottom: 16px; padding: 16px; background: #fff3e0; border-radius: 8px; flex-wrap: wrap;">
            <div style="text-align: center; min-width: 80px;">
              <div style="font-size: 28px; font-weight: bold; color: #e65100;">{{ noWorkDialog.items.length }}</div>
              <div style="font-size: 11px; color: #666; text-transform: uppercase;">узлов</div>
            </div>
            <div style="border-left: 2px solid #ffcc80;"></div>
            <div style="text-align: center; min-width: 80px;">
              <div style="font-size: 28px; font-weight: bold; color: #f57c00;">{{ noWorkDialogUniqueParents }}</div>
              <div style="font-size: 11px; color: #666; text-transform: uppercase;">веток</div>
            </div>
            <div style="border-left: 2px solid #ffcc80;"></div>
            <div style="flex: 1; display: flex; flex-direction: column; justify-content: center; min-width: 200px;">
              <div style="font-size: 11px; color: #888; margin-bottom: 4px;">Путь:</div>
              <div style="font-size: 13px; color: #333; display: flex; align-items: center; flex-wrap: wrap; gap: 4px;">
                <template v-for="(crumb, idx) in (noWorkDialog.breadcrumbs || [])" :key="idx">
                  <span :style="{ fontWeight: idx === noWorkDialog.breadcrumbs.length - 1 ? '600' : '400', color: idx === noWorkDialog.breadcrumbs.length - 1 ? '#e65100' : '#666' }">{{ crumb }}</span>
                  <q-icon v-if="idx < noWorkDialog.breadcrumbs.length - 1" name="chevron_right" size="14px" color="grey" />
                </template>
              </div>
            </div>
          </div>

          <div v-if="noWorkDialog.items.length === 0" style="text-align: center; padding: 40px; color: #999;">
            <q-icon name="check_circle" color="green" size="48px" />
            <div style="margin-top: 12px;">Все узлы содержат работы</div>
          </div>

          <!-- Простой список конечных узлов -->
          <div v-else style="max-height: 450px; overflow-y: auto; font-size: 13px;">
            <div 
              v-for="(item, index) in noWorkDialog.items" 
              :key="index"
              style="padding: 8px 12px; border-bottom: 1px solid #f0f0f0; display: flex; align-items: center; gap: 8px;"
            >
              <q-icon name="warning" color="orange" size="16px" style="flex-shrink: 0;" />
              <span style="color: #888;">{{ item.pathWithoutLast }}</span>
              <span v-if="item.pathWithoutLast" style="color: #888;"> → </span>
              <span style="color: #e65100; font-weight: 600;">{{ item.label }}</span>
            </div>
          </div>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Закрыть" color="primary" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог отправок оперфакта -->
    <q-dialog v-model="operfactDialog.show">
      <q-card style="width: 1200px; max-width: 95vw; max-height: 80vh;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ operfactDialog.title }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section>
          <q-linear-progress v-if="operfactDialog.loading" indeterminate />
          
          <q-table
            v-if="!operfactDialog.loading && operfactDialog.items.length > 0"
            :rows="operfactDialog.items"
            :columns="operfactColumns"
            row-key="usp_fact_id"
            flat
            bordered
            dense
            :rows-per-page-options="[25, 50, 100, 0]"
            :pagination="{ rowsPerPage: 50 }"
          >
            <template v-slot:body-cell-is_sent="props">
              <q-td :props="props">
                <q-icon 
                  :name="props.row.is_sent ? 'check_circle' : 'cancel'" 
                  :color="props.row.is_sent ? 'green' : 'grey'" 
                  size="20px"
                />
              </q-td>
            </template>
            <template v-slot:body-cell-value_date="props">
              <q-td :props="props">
                {{ formatDate(props.row.value_date) }}
              </q-td>
            </template>
            <template v-slot:body-cell-sent_at="props">
              <q-td :props="props">
                {{ props.row.sent_at ? formatDateTime(props.row.sent_at) : '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-fact_value="props">
              <q-td :props="props">
                {{ props.row.fact_value != null ? Number(props.row.fact_value).toLocaleString('ru-RU') : '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-fact_price="props">
              <q-td :props="props">
                {{ props.row.fact_price != null ? Number(props.row.fact_price).toLocaleString('ru-RU') : '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-materials_price="props">
              <q-td :props="props">
                {{ props.row.materials_price != null ? Number(props.row.materials_price).toLocaleString('ru-RU') : '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-is_complete="props">
              <q-td :props="props">
                <q-icon 
                  :name="props.row.is_complete ? 'check_circle' : 'cancel'" 
                  :color="props.row.is_complete ? 'green' : 'grey'" 
                  size="20px"
                />
              </q-td>
            </template>
            <template v-slot:body-cell-materials="props">
              <q-td :props="props">
                <q-btn 
                  icon="inventory_2" 
                  flat 
                  dense 
                  size="sm"
                  color="primary"
                  @click="loadFactMaterials(props.row)"
                >
                  <q-tooltip>Показать материалы</q-tooltip>
                </q-btn>
              </q-td>
            </template>
          </q-table>

          <div v-if="!operfactDialog.loading && operfactDialog.items.length === 0" class="text-center q-pa-lg">
            <q-icon name="info" color="grey-5" size="48px" />
            <p class="text-grey-6 q-mt-md">Нет данных по отправкам</p>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог материалов оперфакта -->
    <q-dialog v-model="materialsDialog.show">
      <q-card style="width: 900px; max-width: 95vw; max-height: 80vh;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ materialsDialog.title }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section>
          <q-linear-progress v-if="materialsDialog.loading" indeterminate />
          <q-table
            v-if="!materialsDialog.loading && materialsDialog.items.length > 0"
            :rows="materialsDialog.items"
            :columns="[
              { name: 'material_id', label: 'ID', field: 'material_id', align: 'center', sortable: true, style: 'width: 70px' },
              { name: 'material_name', label: 'Название', field: 'material_name', align: 'left', sortable: true },
              { name: 'material_value', label: 'Объём', field: 'material_value', align: 'right', sortable: true, style: 'width: 100px' },
              { name: 'consumption_rate', label: 'Расход', field: 'consumption_rate', align: 'right', sortable: true, style: 'width: 100px' },
              { name: 'material_price', label: 'Цена', field: 'material_price', align: 'right', sortable: true, style: 'width: 120px' },
              { name: 'material_fact_price', label: 'Факт цена', field: 'material_fact_price', align: 'right', sortable: true, style: 'width: 120px' },
              { name: 'is_manual', label: 'Ручной', field: 'is_manual', align: 'center', sortable: true, style: 'width: 80px' },
            ]"
            row-key="usp_fact_material_id"
            flat
            bordered
            dense
            :rows-per-page-options="[25, 50, 0]"
            :pagination="{ rowsPerPage: 50 }"
          >
            <template v-slot:body-cell-material_value="props">
              <q-td :props="props">
                {{ props.row.material_value != null ? Number(props.row.material_value).toLocaleString('ru-RU') : '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-material_price="props">
              <q-td :props="props">
                {{ props.row.material_price != null ? Number(props.row.material_price).toLocaleString('ru-RU') : '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-material_fact_price="props">
              <q-td :props="props">
                {{ props.row.material_fact_price != null ? Number(props.row.material_fact_price).toLocaleString('ru-RU') : '-' }}
              </q-td>
            </template>
            <template v-slot:body-cell-is_manual="props">
              <q-td :props="props">
                <q-icon :name="props.row.is_manual ? 'check' : 'close'" :color="props.row.is_manual ? 'green' : 'grey'" size="18px" />
              </q-td>
            </template>
          </q-table>
          <div v-if="!materialsDialog.loading && materialsDialog.items.length === 0" class="text-center q-pa-lg">
            <q-icon name="info" color="grey-5" size="48px" />
            <p class="text-grey-6 q-mt-md">Нет материалов</p>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог деталей Сметы/Договора -->
    <q-dialog v-model="uspDetailDialog.show">
      <q-card style="min-width: 500px; max-width: 800px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ uspDetailDialog.title }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-card-section v-if="uspDetailDialog.node">
          <q-list bordered separator>
            <q-item>
              <q-item-section>
                <q-item-label caption>Статус</q-item-label>
                <q-item-label>
                  <q-icon 
                    :name="getUspDetailStatus.icon" 
                    :color="getUspDetailStatus.color" 
                    size="20px" 
                    class="q-mr-sm"
                  />
                  {{ getUspDetailStatus.text }}
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="getUspDetailData.sendDatetime">
              <q-item-section>
                <q-item-label caption>Дата отправки</q-item-label>
                <q-item-label>{{ formatDateTime(getUspDetailData.sendDatetime) }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="getUspDetailData.result">
              <q-item-section>
                <q-item-label caption>Результат</q-item-label>
                <q-item-label>{{ getUspDetailData.result }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="getUspDetailData.error">
              <q-item-section>
                <q-item-label caption>Ошибка</q-item-label>
                <q-item-label style="color: #d32f2f;">{{ getUspDetailData.error }}</q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-if="getUspDetailData.jsonLoading">
              <q-item-section>
                <q-item-label caption>Отправленные данные</q-item-label>
                <q-item-label>
                  <q-spinner color="primary" size="20px" class="q-mr-sm" />
                  Загрузка JSON...
                </q-item-label>
              </q-item-section>
            </q-item>
            <q-item v-else-if="getUspDetailData.json">
              <q-item-section>
                <q-item-label caption>Отправленные данные</q-item-label>
                <q-item-label>
                  <q-btn 
                    label="Показать JSON" 
                    icon="code" 
                    flat 
                    dense 
                    color="primary"
                    @click="showJson(getUspDetailData.json, uspDetailDialog.type === 'estimate' ? 'JSON Сметы' : 'JSON Договора')"
                  />
                </q-item-label>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>

        <q-card-actions align="right">
          <q-btn flat label="Закрыть" color="primary" v-close-popup />
        </q-card-actions>
      </q-card>
    </q-dialog>

    <!-- Диалог просмотра JSON -->
    <q-dialog v-model="jsonDialog.show">
      <q-card style="min-width: 600px; max-width: 900px;">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">{{ jsonDialog.title }}</div>
          <q-space />
          <q-btn icon="content_copy" flat round dense @click="copyJson">
            <q-tooltip>Копировать</q-tooltip>
          </q-btn>
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>
        <q-card-section>
          <pre style="background: #f5f5f5; padding: 16px; border-radius: 8px; overflow-x: auto; max-height: 500px; font-size: 12px;">{{ jsonDialog.content }}</pre>
        </q-card-section>
      </q-card>
    </q-dialog>

    <!-- Диалог результатов проверки отправки -->
    <q-dialog v-model="checkSendDialog.show" maximized>
      <q-card>
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Проверка готовности отправки</div>
          <q-space />
          <div v-if="checkSendTab === 'results'" class="check-send-summary-panel">
            <q-badge rounded color="green" class="check-send-summary-badge" :label="`Готово ${checkSendSummary.ok}`" />
            <q-badge rounded color="blue-grey" class="check-send-summary-badge" :label="`Не настроен ${checkSendSummary.not_configured}`" />
            <q-badge rounded color="orange" class="check-send-summary-badge" :label="`Блок ${checkSendSummary.not_linked_block}`" />
            <q-badge rounded color="orange-8" class="check-send-summary-badge" :label="`Этаж ${checkSendSummary.not_linked_floor}`" />
            <q-badge rounded color="red" class="check-send-summary-badge" :label="`Ошибка ${checkSendSummary.work_search_error}`" />
          </div>
          <div v-if="checkSendTab === 'smeta_summary'" class="check-send-summary-panel">
            <q-badge rounded color="green" class="check-send-summary-badge" :label="`Готово ${smetaSummarySummary.ok}`" />
            <q-badge rounded color="blue-grey" class="check-send-summary-badge" :label="`Не настроен ${smetaSummarySummary.not_configured}`" />
            <q-badge rounded color="orange" class="check-send-summary-badge" :label="`Блок ${smetaSummarySummary.not_linked_block}`" />
            <q-badge rounded color="orange-8" class="check-send-summary-badge" :label="`Этаж ${smetaSummarySummary.not_linked_floor}`" />
            <q-badge rounded color="red" class="check-send-summary-badge" :label="`Ошибка ${smetaSummarySummary.work_search_error}`" />
          </div>
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-tabs
          v-model="checkSendTab"
          dense
          align="left"
          narrow-indicator
          class="check-send-tabs"
          active-color="primary"
          indicator-color="transparent"
        >
          <q-tab name="results" no-caps class="check-send-tab">
            <div class="check-send-tab-content">
              <q-icon name="fact_check" size="16px" />
              <span>Результаты</span>
              <q-badge
                rounded
                color="primary"
                text-color="white"
                class="check-send-tab-badge"
                :label="checkSendDialog.items.length"
              />
            </div>
          </q-tab>
          <q-tab name="settings" no-caps class="check-send-tab">
            <div class="check-send-tab-content">
              <q-icon name="tune" size="16px" />
              <span>Настройки разделов</span>
              <q-badge
                rounded
                :color="sectionMapUnconfiguredCount > 0 ? 'orange' : 'grey-7'"
                text-color="white"
                class="check-send-tab-badge"
                :label="sectionMapUnconfiguredCount > 0 ? `${sectionMapRows.length} / ${sectionMapUnconfiguredCount} без настройки` : sectionMapRows.length"
              >
                <q-tooltip v-if="sectionMapUnconfiguredCount > 0">{{ sectionMapUnconfiguredCount }} тип(ов) проекта без настройки разделов</q-tooltip>
              </q-badge>
            </div>
          </q-tab>
          <q-tab name="smeta_summary" no-caps class="check-send-tab">
            <div class="check-send-tab-content">
              <q-icon name="receipt_long" size="16px" />
              <span>Сводка сметы</span>
              <q-badge
                v-if="smetaSummaryItems.length"
                rounded
                color="teal"
                text-color="white"
                class="check-send-tab-badge"
                :label="smetaSummaryItems.length"
              />
            </div>
          </q-tab>
        </q-tabs>

        <q-tab-panels v-model="checkSendTab" animated style="flex: 1; overflow: hidden;">
          <q-tab-panel name="results" style="padding: 16px;">
            <div class="check-send-toolbar">
              <div class="check-send-status-filters">
                <q-btn
                  v-for="option in checkSendStatusOptions"
                  :key="option.value"
                  :color="checkSendStatusFilter === option.value ? option.color : 'grey-2'"
                  :text-color="checkSendStatusFilter === option.value ? 'white' : 'grey-9'"
                  :outline="checkSendStatusFilter !== option.value"
                  :unelevated="checkSendStatusFilter === option.value"
                  no-caps
                  rounded
                  dense
                  class="check-send-status-btn"
                  @click="checkSendStatusFilter = option.value"
                >
                  <span>{{ option.label }}</span>
                  <q-badge
                    rounded
                    :color="checkSendStatusFilter === option.value ? 'white' : option.color"
                    :text-color="checkSendStatusFilter === option.value ? option.color : 'white'"
                    class="q-ml-sm"
                    :label="option.count"
                  />
                </q-btn>
              </div>
              <q-select
                v-model="checkSendProjectTypeFilter"
                :options="checkSendProjectTypeOptions"
                class="check-send-project-type-select"
                dense
                outlined
                emit-value
                map-options
                options-dense
                style="width: 260px;"
                label="Тип проекта"
              />
              <q-input v-model="checkSendFilter" dense outlined placeholder="Поиск..." class="check-send-search-input" debounce="300">
                <template v-slot:append>
                  <q-icon name="search" />
                </template>
              </q-input>
            </div>

            <div v-if="checkSendGroupedByProject.length === 0" class="text-center q-pa-xl text-grey-6">
              Ничего не найдено по текущему фильтру
            </div>

            <div v-else class="check-send-scroll">
              <q-expansion-item
                v-for="group in checkSendGroupedByProject"
                :key="group.project_key"
                :default-opened="checkSendGroupedByProject.length <= 3"
                expand-separator
                dense
                header-class="bg-grey-2 check-send-group-header"
              >
                <template v-slot:header>
                  <q-item-section>
                    <div class="check-send-project-header">
                      <div class="check-send-project-title">
                        {{ group.project_name || 'Без названия' }}
                      </div>
                      <div class="check-send-project-meta">
                        <q-badge
                          color="primary"
                          text-color="white"
                          class="check-send-project-id"
                          :label="`ID ${group.project_id || '—'}`"
                        />
                        <q-badge
                          color="indigo-1"
                          text-color="indigo-9"
                          class="check-send-project-type-badge"
                        >
                          <q-icon name="layers" size="14px" class="q-mr-xs" />
                          {{ group.project_type_name || 'Без типа проекта' }}
                        </q-badge>
                        <q-badge
                          color="grey-2"
                          text-color="grey-8"
                          class="check-send-project-count-badge"
                          :label="`Строк ${group.items.length}`"
                        />
                        <q-badge
                          :color="group.main_status_color"
                          text-color="white"
                          class="check-send-project-main-status"
                          :label="group.main_status_label"
                        />
                      </div>
                    </div>
                  </q-item-section>
                  <q-item-section side class="check-send-project-side">
                    <div class="check-send-project-summary">
                      <q-badge color="green" class="check-send-project-summary-badge" :label="`Готово ${group.summary.ok}`" />
                      <q-badge v-if="group.summary.not_configured" color="blue-grey" class="check-send-project-summary-badge" :label="`Не настроен ${group.summary.not_configured}`" />
                      <q-badge color="orange" class="check-send-project-summary-badge" :label="`Блок ${group.summary.not_linked_block}`" />
                      <q-badge color="orange-8" class="check-send-project-summary-badge" :label="`Этаж ${group.summary.not_linked_floor}`" />
                      <q-badge color="red" class="check-send-project-summary-badge" :label="`Ошибка ${group.summary.work_search_error}`" />
                    </div>
                  </q-item-section>
                </template>

                <q-table
                  class="check-send-table"
                  :rows="group.items"
                  :columns="checkSendColumns"
                  row-key="check_send_row_key"
                  :rows-per-page-options="[20, 50, 0]"
                  :pagination="{ rowsPerPage: 20, sortBy: 'project_id', descending: false }"
                  table-style="table-layout: fixed; width: 100%;"
                  dense
                  flat
                  bordered
                >
                  <template v-slot:body-cell-status="props">
                    <q-td :props="props">
                      <q-badge
                        :color="checkSendStatusColor(props.row.status)"
                        :label="props.row.status_label || checkSendStatusText(props.row.status)"
                      />
                    </q-td>
                  </template>
                  <template v-slot:body-cell-error_message="props">
                    <q-td :props="props" style="white-space: normal; word-break: break-word; max-width: 320px; font-size: 12px;">
                      {{ props.row.error_message }}
                    </q-td>
                  </template>
                  <template v-slot:body-cell-expected_section="props">
                    <q-td :props="props" style="white-space: normal; word-break: break-word; max-width: 220px;">
                      <span
                        v-if="props.row.expected_section"
                        :style="props.row.status !== 'ok' ? 'color: #e53935; font-weight: 500;' : 'color: #388e3c;'"
                      >
                        {{ props.row.expected_section }}
                      </span>
                      <span v-else style="color: #bbb;">—</span>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-work_guid="props">
                    <q-td :props="props" style="white-space: normal; word-break: break-all; max-width: 240px;">
                      <span v-if="props.row.work_guid" style="font-family: monospace; font-size: 11px;">
                        {{ props.row.work_guid }}
                      </span>
                      <span v-else style="color: #bbb;">—</span>
                    </q-td>
                  </template>
                </q-table>
              </q-expansion-item>
            </div>
          </q-tab-panel>

          <q-tab-panel name="settings" style="padding: 16px;">
            <div style="display: flex; align-items: center; gap: 8px; margin-bottom: 12px;">
              <q-btn
                label="Добавить"
                icon="add"
                color="primary"
                dense
                no-caps
                @click="openSectionMapForm(null)"
              />
              <q-btn
                icon="refresh"
                flat
                dense
                round
                :loading="sectionMapLoading"
                @click="loadSectionMapSilently"
              >
                <q-tooltip>Обновить</q-tooltip>
              </q-btn>
            </div>

            <q-table
              :rows="sectionMapRows"
              :columns="sectionMapColumns"
              row-key="project_type_section_map_id"
              :rows-per-page-options="[0]"
              :pagination="{ rowsPerPage: 0 }"
              :loading="sectionMapLoading"
              dense
              flat
              bordered
            >
              <template v-slot:body-cell-actions="props">
                <q-td :props="props" class="q-gutter-xs">
                  <q-btn
                    icon="edit"
                    flat
                    dense
                    round
                    size="sm"
                    color="primary"
                    @click="openSectionMapForm(props.row)"
                  >
                    <q-tooltip>Редактировать</q-tooltip>
                  </q-btn>
                  <q-btn
                    icon="delete"
                    flat
                    dense
                    round
                    size="sm"
                    color="negative"
                    @click="confirmDeleteSectionMap(props.row)"
                  >
                    <q-tooltip>Удалить</q-tooltip>
                  </q-btn>
                </q-td>
              </template>
              <template v-slot:body-cell-required_node_name="props">
                <q-td :props="props">
                  <span v-if="props.row.required_node_name">{{ props.row.required_node_name }}</span>
                  <span v-else style="color: #bbb;">—</span>
                </q-td>
              </template>
            </q-table>

            <!-- Форма добавления/редактирования -->
            <q-dialog v-model="sectionMapForm.show" persistent>
              <q-card style="min-width: 460px;">
                <q-card-section>
                  <div class="text-h6">{{ sectionMapForm.project_type_section_map_id ? 'Редактирование' : 'Добавление' }} раздела</div>
                </q-card-section>
                <q-card-section class="q-gutter-sm">
                  <q-select
                    v-model="sectionMapForm.project_type_code"
                    :options="sectionMapProjectTypeOptions"
                    label="Тип проекта"
                    dense
                    outlined
                    emit-value
                    map-options
                    options-dense
                    :rules="[v => !!v || 'Обязательное поле']"
                  />
                  <q-input
                    v-model="sectionMapForm.section_name"
                    label="Раздел УСП"
                    dense
                    outlined
                    :rules="[v => !!v || 'Обязательное поле']"
                  />
                  <q-input
                    v-model.number="sectionMapForm.priority"
                    label="Приоритет"
                    dense
                    outlined
                    type="number"
                  />
                  <q-input
                    v-model="sectionMapForm.required_node_name"
                    label="Обязательный узел (необязательно)"
                    dense
                    outlined
                  />
                </q-card-section>
                <q-card-actions align="right">
                  <q-btn flat label="Отмена" no-caps v-close-popup />
                  <q-btn
                    label="Сохранить"
                    color="primary"
                    no-caps
                    :loading="sectionMapSaving"
                    @click="saveSectionMap"
                  />
                </q-card-actions>
              </q-card>
            </q-dialog>
          </q-tab-panel>

          <q-tab-panel name="smeta_summary" style="padding: 16px;">
            <template v-if="checkSendTab === 'smeta_summary'">
            <q-linear-progress v-if="smetaSummaryLoading" indeterminate />

            <div class="check-send-toolbar" v-if="!smetaSummaryLoading">
              <div class="check-send-status-filters">
                <q-btn
                  v-for="option in smetaSummaryStatusOptions"
                  :key="option.value"
                  :color="smetaSummaryStatusFilter === option.value ? option.color : 'grey-2'"
                  :text-color="smetaSummaryStatusFilter === option.value ? 'white' : 'grey-9'"
                  :outline="smetaSummaryStatusFilter !== option.value"
                  :unelevated="smetaSummaryStatusFilter === option.value"
                  no-caps
                  rounded
                  dense
                  class="check-send-status-btn"
                  @click="smetaSummaryStatusFilter = option.value"
                >
                  <span>{{ option.label }}</span>
                  <q-badge
                    rounded
                    :color="smetaSummaryStatusFilter === option.value ? 'white' : option.color"
                    :text-color="smetaSummaryStatusFilter === option.value ? option.color : 'white'"
                    class="q-ml-sm"
                    :label="option.count"
                  />
                </q-btn>
              </div>
              <q-select
                v-model="smetaSummaryContractorFilter"
                :options="smetaSummaryContractorOptions"
                class="check-send-project-type-select"
                dense
                outlined
                emit-value
                map-options
                options-dense
                style="width: 260px;"
                label="Подрядчик"
              />
              <q-input v-model="smetaSummaryFilter" dense outlined placeholder="Поиск..." class="check-send-search-input" debounce="300">
                <template v-slot:append>
                  <q-icon name="search" />
                </template>
              </q-input>
            </div>

            <div v-if="!smetaSummaryLoading && smetaSummaryGrouped.length === 0" class="text-center q-pa-xl text-grey-6">
              Нет данных по сводке сметы
            </div>

            <div v-else-if="!smetaSummaryLoading" class="check-send-scroll">
              <q-expansion-item
                v-for="group in smetaSummaryGrouped"
                :key="group.project_key"
                :default-opened="smetaSummaryGrouped.length <= 3"
                expand-separator
                dense
                header-class="bg-grey-2 check-send-group-header"
              >
                <template v-slot:header>
                  <q-item-section>
                    <div class="check-send-project-header">
                      <div class="check-send-project-title">
                        {{ group.project_name || 'Без названия' }}
                      </div>
                      <div class="check-send-project-meta">
                        <q-badge
                          color="primary"
                          text-color="white"
                          class="check-send-project-id"
                          :label="`ID ${group.project_id || '—'}`"
                        />
                        <q-badge
                          color="grey-2"
                          text-color="grey-8"
                          class="check-send-project-count-badge"
                          :label="`Строк ${group.items.length}`"
                        />
                        <q-badge
                          :color="group.main_status_color"
                          text-color="white"
                          class="check-send-project-main-status"
                          :label="group.main_status_label"
                        />
                      </div>
                    </div>
                  </q-item-section>
                  <q-item-section side class="check-send-project-side">
                    <div class="smeta-summary-side">
                      <div class="check-send-project-summary" style="margin-right: 16px;">
                        <q-badge color="green" class="check-send-project-summary-badge" :label="`Готово ${group.summary.ok}`" />
                        <q-badge v-if="group.summary.not_configured" color="blue-grey" class="check-send-project-summary-badge" :label="`Не настроен ${group.summary.not_configured}`" />
                        <q-badge v-if="group.summary.not_linked_block" color="orange" class="check-send-project-summary-badge" :label="`Блок ${group.summary.not_linked_block}`" />
                        <q-badge v-if="group.summary.not_linked_floor" color="orange-8" class="check-send-project-summary-badge" :label="`Этаж ${group.summary.not_linked_floor}`" />
                        <q-badge v-if="group.summary.work_search_error" color="red" class="check-send-project-summary-badge" :label="`Ошибка ${group.summary.work_search_error}`" />
                      </div>
                      <div class="smeta-summary-side-item">
                        <span class="smeta-summary-side-label">Смета без НДС</span>
                        <span class="smeta-summary-side-value">{{ formatSmetaSum(group.smeta_total_sum) }}</span>
                      </div>
                      <div class="smeta-summary-side-divider" />
                      <div class="smeta-summary-side-item">
                        <span class="smeta-summary-side-label">Договор</span>
                        <span class="smeta-summary-side-value">{{ formatSmetaSum(group.agreement_sum) }}</span>
                      </div>
                    </div>
                  </q-item-section>
                </template>

                <q-table
                  class="check-send-table smeta-summary-q-table"
                  :rows="group.items"
                  :columns="smetaSummaryColumns"
                  row-key="smeta_row_key"
                  :rows-per-page-options="[0]"
                  :pagination="{ rowsPerPage: 0 }"
                  table-style="table-layout: fixed; width: 100%;"
                  style="max-height: min(440px, 50vh);"
                  virtual-scroll
                  :virtual-scroll-item-size="36"
                  :virtual-scroll-sticky-size-start="36"
                  hide-bottom
                  dense
                  flat
                  bordered
                >
                  <template v-slot:body-cell-status="props">
                    <q-td :props="props">
                      <q-badge
                        :color="checkSendStatusColor(props.row.status)"
                        :label="props.row.status_label || checkSendStatusText(props.row.status)"
                      />
                    </q-td>
                  </template>
                  <template v-slot:body-cell-contractor_name="props">
                    <q-td :props="props">
                      <q-badge
                        v-if="props.row.contractor_name"
                        color="teal-1"
                        text-color="teal-9"
                        style="font-size: 11px; padding: 3px 6px;"
                      >
                        {{ props.row.contractor_name }}
                      </q-badge>
                      <span v-else style="color: #bbb;">—</span>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-work_sum_without_nds="props">
                    <q-td :props="props" style="text-align: right;">
                      {{ formatSmetaSum(props.row.work_sum_without_nds) }}
                    </q-td>
                  </template>
                  <template v-slot:body-cell-material_sum_without_nds="props">
                    <q-td :props="props" style="text-align: right;">
                      {{ formatSmetaSum(props.row.material_sum_without_nds) }}
                    </q-td>
                  </template>
                  <template v-slot:body-cell-total_sum_without_nds="props">
                    <q-td :props="props" style="text-align: right; font-weight: 600;">
                      {{ formatSmetaSum(props.row.total_sum_without_nds) }}
                    </q-td>
                  </template>
                  <template v-slot:body-cell-usp_block_name="props">
                    <q-td :props="props">
                      <span v-if="props.row.usp_block_name">{{ props.row.usp_block_name }}</span>
                      <span v-else style="color: #bbb;">—</span>
                    </q-td>
                  </template>
                  <template v-slot:body-cell-usp_level_name="props">
                    <q-td :props="props">
                      <span v-if="props.row.usp_level_name">{{ props.row.usp_level_name }}</span>
                      <span v-else style="color: #bbb;">—</span>
                    </q-td>
                  </template>
                </q-table>
              </q-expansion-item>
            </div>
            </template>
          </q-tab-panel>
        </q-tab-panels>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { getProjectPattern, getProjectByGuid, getProjectUspJson, updateEntranceLink, updateFloorLink, getProjectGroups, ingestProjectGroups, ingestProjectPattern, getUspFloorMapSend, getUspFactForOperfact, getUspFactMaterialByFact, checkSendReadiness, getSectionMap, modifySectionMap, deleteSectionMap, getSectionMapProjectTypes, getSmetaSummary } from './services';
import { Notify } from 'quasar';
export default {
  name: "ProjectPatternStructure",
  components: {},
  data() {
    return {
      projectGuid: '',
      selectedProjectName: '',
      selectedProjectUrl: '',
      isLoading: false,
      isSilentLoading: false,
      isLoadingProjects: false,
      isIngestingProjects: false,
      isRefreshingPattern: false,
      treeData: [],
      projectTreeData: [],
      expanded: [],
      expandedProjects: [],
      splitterModel: 50,
      draggedNode: null, // оставляем для совместимости, но не используем в UI
      dropTargetNode: null, // оставляем для совместимости, но не используем в UI
      selectedLeftNode: null,
      selectedRightNode: null,
      projectSelectorDialog: {
        show: false
      },
      projectSearchQuery: '',
      projectGroups: [],
      projectColumns: [
        {
          name: 'project_name',
          label: 'Название проекта',
          field: 'project_name',
          align: 'left',
          sortable: true
        },
        {
          name: 'project_type',
          label: 'Тип проекта',
          field: 'project_type',
          align: 'left',
          sortable: true
        },
        {
          name: 'project_guid',
          label: 'GUID',
          field: 'project_guid',
          align: 'left',
          sortable: false
        },
        {
          name: 'actions',
          label: 'Действия',
          field: 'actions',
          align: 'center'
        }
      ],
      confirmDialog: {
        show: false,
        title: '',
        message: '',
        onConfirm: null
      },
      linkConfirmDialog: {
        show: false,
        leftNode: null,
        rightNode: null,
        leftPath: [],
        rightPath: [],
        onConfirm: null
      },
      linkedItemsDialog: {
        show: false,
        nodeName: '',
        items: []
      },
      linkPathDialog: {
        show: false,
        sourceName: '',
        targetName: '',
        path: []
      },
      selectedEntranceForFloors: null, // Выбранный подъезд для связки этажей
      filteredTreeData: [], // Отфильтрованное дерево УСП
      showOnlyUnlinkedLeft: false, // Показывать только несвязанные слева
      showOnlyUnlinkedRight: false, // Показывать только несвязанные справа
      noWorkDialog: {
        show: false,
        nodeName: '',
        breadcrumbs: [],
        items: []
      },
      operfactDialog: {
        show: false,
        loading: false,
        title: '',
        items: []
      },
      materialsDialog: {
        show: false,
        loading: false,
        items: [],
        title: ''
      },
      jsonDialog: {
        show: false,
        title: '',
        content: ''
      },
      uspDetailDialog: {
        show: false,
        title: '',
        type: '',
        node: null,
        uspJson: null,
        uspJsonLoading: false
      },
      isCheckingSend: false,
      checkSendDialog: {
        show: false,
        items: []
      },
      checkSendFilter: '',
      checkSendStatusFilter: 'all',
      checkSendProjectTypeFilter: 'all',
      checkSendTab: 'results',
      smetaSummaryItems: [],
      smetaSummaryLoading: false,
      smetaSummaryFilter: '',
      smetaSummaryStatusFilter: 'all',
      smetaSummaryContractorFilter: 'all',
      sectionMapRows: [],
      sectionMapProjectTypes: [],
      sectionMapLoading: false,
      sectionMapSaving: false,
      sectionMapForm: {
        show: false,
        project_type_section_map_id: null,
        project_type_code: null,
        section_name: '',
        priority: 1,
        required_node_name: '',
      },
      sectionMapColumns: [
        { name: 'project_type_code', label: 'Код типа проекта', field: 'project_type_code', align: 'left', sortable: true },
        { name: 'project_type_name', label: 'Название типа проекта', field: 'project_type_name', align: 'left', sortable: true },
        { name: 'section_name', label: 'Раздел УСП', field: 'section_name', align: 'left', sortable: true },
        { name: 'priority', label: 'Приоритет', field: 'priority', align: 'center', sortable: true, style: 'width: 90px' },
        { name: 'required_node_name', label: 'Обяз. узел', field: 'required_node_name', align: 'left', sortable: true },
        { name: 'actions', label: 'Действия', field: 'actions', align: 'center', style: 'width: 100px' },
      ],
      checkSendColumns: [
        { name: 'status', label: 'Статус', field: 'status', align: 'center', sortable: true, style: 'width: 90px' },
        { name: 'block_name', label: 'Блок', field: 'block_name', align: 'center', sortable: true, style: 'width: 70px' },
        { name: 'entrance', label: 'Подъезд', field: 'entrance', align: 'center', sortable: true, style: 'width: 70px' },
        { name: 'level_num', label: 'Этаж', field: 'level_num', align: 'center', sortable: true, style: 'width: 60px' },
        { name: 'floor_map_id', label: 'floor_map_id', field: 'floor_map_id', align: 'center', sortable: true, style: 'width: 90px' },
        { name: 'usp_block_name', label: 'Узел блока УСП', field: 'usp_block_name', align: 'left', sortable: true, style: 'width: 160px' },
        { name: 'usp_level_name', label: 'Узел этажа УСП', field: 'usp_level_name', align: 'left', sortable: true, style: 'width: 160px' },
        { name: 'expected_section', label: 'Ожидаемый раздел', field: 'expected_section', align: 'left', sortable: true, style: 'width: 180px' },
        { name: 'work_guid', label: 'work_guid', field: 'work_guid', align: 'left', sortable: false, style: 'width: 220px' },
        { name: 'error_message', label: 'Ошибка', field: 'error_message', align: 'left', sortable: true, style: 'width: 320px' },
      ],
      smetaSummaryColumns: [
        { name: 'status', label: 'Статус', field: 'status', align: 'center', sortable: true, style: 'width: 90px' },
        { name: 'contractor_name', label: 'Подрядчик', field: 'contractor_name', align: 'left', sortable: true, style: 'width: 150px' },
        { name: 'block_name', label: 'Блок', field: 'block_name', align: 'center', sortable: true, style: 'width: 80px' },
        { name: 'entrance', label: 'Подъезд', field: 'entrance', align: 'center', sortable: true, style: 'width: 70px' },
        { name: 'level_num', label: 'Этаж', field: 'level_num', align: 'center', sortable: true, style: 'width: 60px' },
        { name: 'floor_map_id', label: 'floor_map_id', field: 'floor_map_id', align: 'center', sortable: true, style: 'width: 90px' },
        { name: 'work_set_name', label: 'Вид работы', field: 'work_set_name', align: 'left', sortable: true, style: 'width: 240px' },
        { name: 'usp_block_name', label: 'Узел блока УСП', field: 'usp_block_name', align: 'left', sortable: true, style: 'width: 140px' },
        { name: 'usp_level_name', label: 'Узел этажа УСП', field: 'usp_level_name', align: 'left', sortable: true, style: 'width: 140px' },
        { name: 'work_sum_without_nds', label: 'СМР без НДС', field: 'work_sum_without_nds', align: 'right', sortable: true, style: 'width: 120px' },
        { name: 'material_sum_without_nds', label: 'ТМЦ без НДС', field: 'material_sum_without_nds', align: 'right', sortable: true, style: 'width: 120px' },
        { name: 'total_sum_without_nds', label: 'Итого без НДС', field: 'total_sum_without_nds', align: 'right', sortable: true, style: 'width: 130px' },
      ],
      operfactColumns: [
        { name: 'usp_fact_id', label: 'ID', field: 'usp_fact_id', align: 'center', sortable: true, style: 'width: 70px' },
        { name: 'is_sent', label: 'Статус', field: 'is_sent', align: 'center', sortable: true, style: 'width: 70px' },
        { name: 'value_date', label: 'Дата факта', field: 'value_date', align: 'left', sortable: true, style: 'width: 100px' },
        { name: 'sent_at', label: 'Дата отправки', field: 'sent_at', align: 'left', sortable: true, style: 'width: 140px' },
        { name: 'level_num', label: 'Этаж', field: 'level_num', align: 'center', sortable: true, style: 'width: 60px' },
        { name: 'block_name', label: 'Блок', field: 'block_name', align: 'center', sortable: true, style: 'width: 80px' },
        { name: 'fact_value', label: 'Объём', field: 'fact_value', align: 'right', sortable: true, style: 'width: 90px' },
        { name: 'fact_price', label: 'СМР', field: 'fact_price', align: 'right', sortable: true, style: 'width: 120px' },
        { name: 'materials_price', label: 'ТМЦ', field: 'materials_price', align: 'right', sortable: true, style: 'width: 120px' },
        { name: 'is_complete', label: 'Завершён', field: 'is_complete', align: 'center', sortable: true, style: 'width: 80px' },
        { name: 'materials', label: 'Материалы', align: 'center', style: 'width: 90px' },
      ],
    };
  },
  watch: {
    checkSendTab(val) {
      if (val === 'settings' && !this.sectionMapRows.length) {
        this.loadSectionMapSilently();
      }
      if (val === 'smeta_summary' && !this.smetaSummaryItems.length) {
        this.loadSmetaSummary();
      }
    },
  },
  computed: {
    activeProjectGuid() {
      return this.projectGuid || '';
    },
    expandedSet() {
      return new Set(this.expanded);
    },
    expandedProjectsSet() {
      return new Set(this.expandedProjects);
    },
    uspGuidToLabelMap() {
      const map = new Map();
      const walk = (nodes) => {
        for (const n of nodes) {
          if (n.guid) map.set(n.guid, n.label);
          if (n.children) walk(n.children);
        }
      };
      walk(this.treeData);
      return map;
    },
    linkedSummaryByGuid() {
      const map = new Map();
      const walk = (nodes) => {
        for (const n of nodes) {
          const isBlockNode = n.project_entrance_id && !n.floor_map_id;
          const isFloorNode = !!n.floor_map_id;
          const guid = isBlockNode ? n.usp_block_guid : (isFloorNode ? n.usp_level_guid : null);
          if (guid) {
            if (!map.has(guid)) {
              map.set(guid, { blockCount: 0, floorCount: 0, totalCount: 0, items: [] });
            }
            const entry = map.get(guid);
            if (isBlockNode) {
              entry.blockCount++;
              entry.items.push({ type: 'entrance', label: n.label, id: n.project_entrance_id, node: n });
            } else if (isFloorNode) {
              entry.floorCount++;
              entry.items.push({ type: 'floor', label: n.label, id: n.floor_map_id, node: n });
            }
            entry.totalCount = entry.blockCount + entry.floorCount;
          }
          if (n.children) walk(n.children);
        }
      };
      walk(this.projectTreeData);
      return map;
    },
    filteredProjectTreeData() {
      if (!this.showOnlyUnlinkedLeft) {
        return this.projectTreeData;
      }
      return this.filterUnlinkedNodes(this.projectTreeData, 'left');
    },
    filteredUspTreeData() {
      if (!this.showOnlyUnlinkedRight) {
        return this.treeData;
      }
      return this.filterUnlinkedNodes(this.treeData, 'right');
    },
    filteredProjects() {
      if (!this.projectSearchQuery) {
        return this.projectGroups;
      }
      
      const query = this.projectSearchQuery.toLowerCase();
      return this.projectGroups.filter(project => {
        return (
          project.project_name?.toLowerCase().includes(query) ||
          project.project_type?.toLowerCase().includes(query) ||
          project.project_guid?.toLowerCase().includes(query)
        );
      });
    },
    canManualLink() {
      const left = this.selectedLeftNode;
      const right = this.selectedRightNode;
      if (!left || !right) return false;

      const draggedEntranceId = left.project_entrance_id;
      const draggedFloorId = left.floor_map_id;
      const targetGuid = right.guid;

      if (!targetGuid) return false;

      // Те же правила, что и для drag&drop:
      // подъезд (блок) или этаж слева, guid-узел УСП справа
      if (!draggedEntranceId && !draggedFloorId) return false;

      // Для этажей дополнительно проверяем фильтр подъезда, как раньше в canDragNode
      if (draggedFloorId) {
        if (!this.selectedEntranceForFloors) {
          return false;
        }
        if (!this.isFloorOfEntrance(left, this.selectedEntranceForFloors)) {
          return false;
        }
      }

      return true;
    },
    groupedMissingWork() {
      // Группируем узлы без работ по пути
      const groups = {};
      
      this.noWorkDialog.items.forEach(item => {
        const path = item.path || '—';
        if (!groups[path]) {
          groups[path] = {
            path: path,
            items: []
          };
        }
        groups[path].items.push(item);
      });
      
      return Object.values(groups);
    },
    noWorkDialogUniqueParents() {
      // Считаем уникальные родительские пути
      const uniquePaths = new Set();
      this.noWorkDialog.items.forEach(item => {
        if (item.pathWithoutLast) {
          uniquePaths.add(item.pathWithoutLast);
        }
      });
      return uniquePaths.size;
    },
    getUspDetailData() {
      const node = this.uspDetailDialog.node;
      if (!node) return {};
      const jsonData = this.uspDetailDialog.uspJson;
      const loading = this.uspDetailDialog.uspJsonLoading;

      if (this.uspDetailDialog.type === 'estimate') {
        return {
          isSend: node.is_send_usp_estimate,
          result: node.usp_estimate_result,
          error: node.usp_estimate_error,
          sendDatetime: node.usp_estimate_send_datetime,
          json: jsonData ? jsonData.usp_estimate_json : null,
          jsonLoading: loading
        };
      } else {
        return {
          isSend: node.is_send_usp_contract,
          result: node.usp_contract_result,
          error: node.usp_contract_error,
          sendDatetime: node.usp_contract_send_datetime,
          json: jsonData ? jsonData.usp_contract_json : null,
          jsonLoading: loading
        };
      }
    },
    getUspDetailStatus() {
      const data = this.getUspDetailData;
      if (data.error) {
        return { icon: 'error', color: 'red', text: 'Ошибка' };
      } else if (data.isSend) {
        return { icon: 'check_circle', color: 'green', text: 'Отправлено' };
      } else {
        return { icon: 'cancel', color: 'grey', text: 'Не отправлено' };
      }
    },
    checkSendSummary() {
      const items = this.checkSendDialog.items || [];
      const summary = { ok: 0, not_configured: 0, not_linked_block: 0, not_linked_floor: 0, work_search_error: 0 };
      for (let i = 0; i < items.length; i++) {
        const s = items[i].status;
        if (s in summary) summary[s]++;
      }
      return summary;
    },
    checkSendProjectTypeOptions() {
      const items = this.checkSendDialog.items || [];
      const uniqueTypes = Array.from(
        new Set(
          items
            .map(i => i.project_type_name)
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b));

      return [
        { label: 'Все типы', value: 'all' },
        ...uniqueTypes.map(type => ({
          label: type,
          value: type,
        })),
      ];
    },
    checkSendStatusOptions() {
      const total = (this.checkSendDialog.items || []).length;
      return [
        { label: 'Все', value: 'all', color: 'primary', count: total },
        { label: 'Готово', value: 'ok', color: 'green', count: this.checkSendSummary.ok },
        { label: 'Не настроен', value: 'not_configured', color: 'blue-grey', count: this.checkSendSummary.not_configured },
        { label: 'Нет связи блока', value: 'not_linked_block', color: 'orange', count: this.checkSendSummary.not_linked_block },
        { label: 'Нет связи этажа', value: 'not_linked_floor', color: 'orange-8', count: this.checkSendSummary.not_linked_floor },
        { label: 'Ошибка поиска работы', value: 'work_search_error', color: 'red', count: this.checkSendSummary.work_search_error },
      ];
    },
    checkSendDialogFiltered() {
      let items = this.checkSendDialog.items || [];

      if (this.checkSendProjectTypeFilter !== 'all') {
        items = items.filter(i => i.project_type_name === this.checkSendProjectTypeFilter);
      }

      if (this.checkSendFilter) {
        const query = this.checkSendFilter.toLowerCase();
        items = items.filter(i => {
          return [
            i.project_id,
            i.project_name,
            i.project_type_name,
            i.block_name,
            i.entrance,
            i.level_num,
            i.floor_map_id,
            i.usp_block_name,
            i.usp_level_name,
            i.work_guid,
            i.error_message,
            i.status_label,
          ].some(v => String(v || '').toLowerCase().includes(query));
        });
      }

      if (this.checkSendStatusFilter !== 'all') {
        items = items.filter(i => i.status === this.checkSendStatusFilter);
      }

      return items
        .map(i => ({
          ...i,
          check_send_row_key: `${i.project_id}_${i.floor_map_id}_${i.status || 'none'}`,
        }))
        .sort((a, b) => {
          const projectCmp = Number(a.project_id || 0) - Number(b.project_id || 0);
          if (projectCmp !== 0) return projectCmp;

          const blockCmp = String(a.block_name || '').localeCompare(String(b.block_name || ''));
          if (blockCmp !== 0) return blockCmp;

          return Number(a.level_num || 0) - Number(b.level_num || 0);
        });
    },
    checkSendGroupedByProject() {
      const groups = new Map();

      this.checkSendDialogFiltered.forEach(item => {
        const key = `${item.project_id || 'no_project'}__${item.project_name || 'Без названия'}__${item.project_type_name || 'Без типа проекта'}`;
        if (!groups.has(key)) {
          groups.set(key, []);
        }
        groups.get(key).push(item);
      });

      return Array.from(groups.entries()).map(([project_key, items]) => {
        const summary = { ok: 0, not_configured: 0, not_linked_block: 0, not_linked_floor: 0, work_search_error: 0 };
        for (let j = 0; j < items.length; j++) {
          const s = items[j].status;
          if (s in summary) summary[s]++;
        }
        const mainStatus = summary.work_search_error
          ? 'work_search_error'
          : summary.not_linked_floor
            ? 'not_linked_floor'
            : summary.not_linked_block
              ? 'not_linked_block'
              : summary.not_configured
                ? 'not_configured'
                : 'ok';

        return {
          project_key,
          project_id: items[0]?.project_id,
          project_name: items[0]?.project_name,
          project_type_name: items[0]?.project_type_name,
          items,
          summary,
          main_status: mainStatus,
          main_status_label: this.checkSendStatusText(mainStatus),
          main_status_color: this.checkSendStatusColor(mainStatus),
        };
      });
    },
    sectionMapProjectTypeOptions() {
      return (this.sectionMapProjectTypes || []).map(pt => ({
        label: `${pt.project_type_name} (${pt.project_type_code})`,
        value: pt.project_type_code,
      }));
    },
    sectionMapByProjectTypeCode() {
      const grouped = {};

      (this.sectionMapRows || []).forEach((row) => {
        if (!row.project_type_code) {
          return;
        }

        if (!grouped[row.project_type_code]) {
          grouped[row.project_type_code] = [];
        }

        grouped[row.project_type_code].push(row);
      });

      Object.keys(grouped).forEach((key) => {
        grouped[key].sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0));
      });

      return grouped;
    },
    smetaSummarySummary() {
      const items = this.smetaSummaryItems || [];
      const summary = { ok: 0, not_configured: 0, not_linked_block: 0, not_linked_floor: 0, work_search_error: 0 };
      for (let i = 0; i < items.length; i++) {
        const s = items[i].status;
        if (s in summary) summary[s]++;
      }
      return summary;
    },
    smetaSummaryStatusOptions() {
      const total = (this.smetaSummaryItems || []).length;
      return [
        { label: 'Все', value: 'all', color: 'primary', count: total },
        { label: 'Готово', value: 'ok', color: 'green', count: this.smetaSummarySummary.ok },
        { label: 'Не настроен', value: 'not_configured', color: 'blue-grey', count: this.smetaSummarySummary.not_configured },
        { label: 'Нет связи блока', value: 'not_linked_block', color: 'orange', count: this.smetaSummarySummary.not_linked_block },
        { label: 'Нет связи этажа', value: 'not_linked_floor', color: 'orange-8', count: this.smetaSummarySummary.not_linked_floor },
        { label: 'Ошибка поиска работы', value: 'work_search_error', color: 'red', count: this.smetaSummarySummary.work_search_error },
      ];
    },
    smetaSummaryContractorOptions() {
      const items = this.smetaSummaryItems || [];
      const contractors = Array.from(
        new Set(items.map(i => i.contractor_name).filter(Boolean))
      ).sort((a, b) => a.localeCompare(b));
      return [
        { label: 'Все подрядчики', value: 'all' },
        ...contractors.map(name => ({ label: name, value: name })),
      ];
    },
    smetaSummaryFiltered() {
      let items = this.smetaSummaryItems || [];
      if (this.smetaSummaryStatusFilter !== 'all') {
        items = items.filter(i => i.status === this.smetaSummaryStatusFilter);
      }
      if (this.smetaSummaryContractorFilter !== 'all') {
        items = items.filter(i => i.contractor_name === this.smetaSummaryContractorFilter);
      }
      if (this.smetaSummaryFilter) {
        const query = this.smetaSummaryFilter.toLowerCase();
        items = items.filter(i =>
          [i.project_id, i.project_name, i.contractor_name, i.block_name, i.entrance, i.level_num, i.floor_map_id, i.work_set_name, i.usp_block_name, i.usp_level_name, i.status_label]
            .some(v => String(v || '').toLowerCase().includes(query))
        );
      }
      return items.slice().sort((a, b) => {
        const pCmp = Number(a.project_id || 0) - Number(b.project_id || 0);
        if (pCmp !== 0) return pCmp;
        const eCmp = Number(a.entrance || 0) - Number(b.entrance || 0);
        if (eCmp !== 0) return eCmp;
        const lCmp = Number(a.level_num || 0) - Number(b.level_num || 0);
        if (lCmp !== 0) return lCmp;
        return String(a.work_set_name || '').localeCompare(String(b.work_set_name || ''));
      });
    },
    smetaSummaryGrouped() {
      const groups = new Map();
      this.smetaSummaryFiltered.forEach(item => {
        const key = `${item.project_id || 'none'}__${item.project_name || ''}`;
        if (!groups.has(key)) {
          groups.set(key, {
            project_key: key,
            project_id: item.project_id,
            project_name: item.project_name,
            smeta_total_sum: item.smeta_total_sum,
            agreement_sum: item.agreement_sum,
            items: [],
          });
        }
        groups.get(key).items.push(item);
      });
      return Array.from(groups.entries()).map(([, group]) => {
        const summary = { ok: 0, not_configured: 0, not_linked_block: 0, not_linked_floor: 0, work_search_error: 0 };
        const gItems = group.items;
        for (let j = 0; j < gItems.length; j++) {
          const s = gItems[j].status;
          if (s in summary) summary[s]++;
        }
        const mainStatus = summary.work_search_error
          ? 'work_search_error'
          : summary.not_linked_floor
            ? 'not_linked_floor'
            : summary.not_linked_block
              ? 'not_linked_block'
              : summary.not_configured
                ? 'not_configured'
                : 'ok';
        return {
          ...group,
          summary,
          main_status_label: this.checkSendStatusText(mainStatus),
          main_status_color: this.checkSendStatusColor(mainStatus),
        };
      });
    },
    sectionMapUnconfiguredCount() {
      const configuredCodes = new Set(
        (this.sectionMapRows || []).map(r => r.project_type_code).filter(Boolean)
      );
      return this.projectTreeData
        .flatMap(jk => jk.children || [])
        .filter(node => node.project_type_code && !configuredCodes.has(node.project_type_code))
        .length;
    },
  },
  methods: {
    isProjectTypeNode(node) {
      return !!node?.project_type_code && !!node?.project_type_id && !node?.project_id && !node?.project_entrance_id && !node?.floor_map_id;
    },
    getProjectTypeSectionSettings(node) {
      if (!this.isProjectTypeNode(node)) {
        return [];
      }

      return this.sectionMapByProjectTypeCode[node.project_type_code] || [];
    },
    getProjectTypeSectionTooltip(node) {
      const settings = this.getProjectTypeSectionSettings(node);

      if (!settings.length) {
        return [
          'Для этого типа проекта не настроен ожидаемый раздел.',
        ];
      }

      return settings.map((item) => {
        const nodePart = item.required_node_name ? `, узел: ${item.required_node_name}` : '';
        return `${item.priority}. ${item.section_name}${nodePart}`;
      });
    },
    checkSendStatusText(status) {
      if (status === 'ok') return 'Готово к отправке';
      if (status === 'not_configured') return 'Не настроен раздел';
      if (status === 'not_linked_block') return 'Не привязан блок';
      if (status === 'not_linked_floor') return 'Не привязан этаж';
      if (status === 'work_search_error') return 'Ошибка поиска работы';
      return status || '—';
    },
    checkSendStatusColor(status) {
      if (status === 'ok') return 'green';
      if (status === 'not_configured') return 'blue-grey';
      if (status === 'not_linked_block') return 'orange';
      if (status === 'not_linked_floor') return 'orange-8';
      if (status === 'work_search_error') return 'red';
      return 'grey';
    },
    async loadSectionMapSilently() {
      this.sectionMapLoading = true;
      try {
        const res = await getSectionMap();
        this.sectionMapRows = res || [];
      } catch (e) {
        Notify.create({ type: 'negative', message: 'Ошибка загрузки настроек разделов', position: 'top' });
      } finally {
        this.sectionMapLoading = false;
      }
    },
    async loadSmetaSummary() {
      if (!this.activeProjectGuid) return;
      this.smetaSummaryLoading = true;
      try {
        const res = await getSmetaSummary(this.activeProjectGuid, null);
        const list = res || [];
        for (let i = 0; i < list.length; i++) {
          const row = list[i];
          row.smeta_row_key = `${row.project_id ?? ''}_${row.floor_map_id ?? ''}_${row.work_set_id ?? ''}_${i}`;
        }
        this.smetaSummaryItems = list;
      } catch (e) {
        Notify.create({ type: 'negative', message: 'Ошибка загрузки сводки сметы', position: 'top' });
      } finally {
        this.smetaSummaryLoading = false;
      }
    },
    formatSmetaSum(val) {
      if (val == null) return '—';
      return Number(val).toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    async loadSectionMapProjectTypes() {
      try {
        const res = await getSectionMapProjectTypes();
        this.sectionMapProjectTypes = res || [];
      } catch (e) {
        this.sectionMapProjectTypes = [];
      }
    },
    openSectionMapForm(row) {
      if (!this.sectionMapProjectTypes.length) {
        this.loadSectionMapProjectTypes();
      }
      if (row) {
        this.sectionMapForm = {
          show: true,
          project_type_section_map_id: row.project_type_section_map_id,
          project_type_code: row.project_type_code,
          section_name: row.section_name,
          priority: row.priority || 1,
          required_node_name: row.required_node_name || '',
        };
      } else {
        this.sectionMapForm = {
          show: true,
          project_type_section_map_id: null,
          project_type_code: null,
          section_name: '',
          priority: 1,
          required_node_name: '',
        };
      }
    },
    async saveSectionMap() {
      if (!this.sectionMapForm.project_type_code || !this.sectionMapForm.section_name) {
        Notify.create({ type: 'warning', message: 'Заполните обязательные поля', position: 'top' });
        return;
      }
      this.sectionMapSaving = true;
      try {
        await modifySectionMap({
          project_type_section_map_id: this.sectionMapForm.project_type_section_map_id,
          project_type_code: this.sectionMapForm.project_type_code,
          section_name: this.sectionMapForm.section_name,
          priority: this.sectionMapForm.priority || 1,
          required_node_name: this.sectionMapForm.required_node_name || null,
        });
        this.sectionMapForm.show = false;
        Notify.create({ type: 'positive', message: 'Сохранено', position: 'top', timeout: 2000 });
        await this.loadSectionMapSilently();
      } catch (e) {
        Notify.create({
          type: 'negative',
          message: e?.response?.data?.message || 'Ошибка сохранения',
          position: 'top',
        });
      } finally {
        this.sectionMapSaving = false;
      }
    },
    confirmDeleteSectionMap(row) {
      this.confirmDialog = {
        show: true,
        title: 'Удаление',
        message: `Удалить раздел «${row.section_name}» для типа «${row.project_type_name || row.project_type_code}»?`,
        onConfirm: () => this.deleteSectionMapRow(row.project_type_section_map_id),
      };
    },
    async deleteSectionMapRow(id) {
      try {
        await deleteSectionMap(id);
        Notify.create({ type: 'positive', message: 'Удалено', position: 'top', timeout: 2000 });
        await this.loadSectionMapSilently();
      } catch (e) {
        Notify.create({
          type: 'negative',
          message: e?.response?.data?.message || 'Ошибка удаления',
          position: 'top',
        });
      }
    },
    clearNodeSelection() {
      this.selectedLeftNode = null;
      this.selectedRightNode = null;
    },
    onLeftNodeClick(node) {
      // Можно выбирать только подъезды и этажи
      if (!this.isDraggableEntrance(node)) {
        return;
      }

      // Для этажей сохраняем прежнее правило: нужен выбранный подъезд-фильтр
      if (!this.canDragNode(node)) {
        Notify.create({
          type: 'warning',
          message: 'Сначала выберите подъезд для фильтрации (кнопка с фильтром)',
          position: 'top',
        });
        return;
      }

      this.selectedLeftNode =
        this.selectedLeftNode && this.selectedLeftNode.id === node.id
          ? null
          : node;
    },
    onRightNodeClick(node) {
      // Справа доступны только узлы с guid (как isDropTarget)
      if (!node.guid || node.project_entrance_id || node.floor_map_id) {
        return;
      }

      this.selectedRightNode =
        this.selectedRightNode && this.selectedRightNode.id === node.id
          ? null
          : node;
    },
    onManualLinkClick() {
      const left = this.selectedLeftNode;
      const right = this.selectedRightNode;

      if (!this.canManualLink || !left || !right) {
        return;
      }

      this.linkConfirmDialog = {
        show: true,
        leftNode: left,
        rightNode: right,
        leftPath: this.buildPathToProjectNode(left),
        rightPath: this.buildPathToNode(right.guid),
        onConfirm: async () => {
          await this.performSingleLink(left, right);
        }
      };
    },
    async onLinkConfirm() {
      this.linkConfirmDialog.show = false;
      if (this.linkConfirmDialog.onConfirm) {
        await this.linkConfirmDialog.onConfirm();
      }
    },
    async loadData() {
      if (!this.projectGuid) return;
      
      this.isLoading = true;
      this.isLoadingProjects = true;
      
      try {
        const [patternRes, projectRes, sectionMapRes] = await Promise.all([
          getProjectPattern(this.projectGuid),
          getProjectByGuid(this.projectGuid, null), // Передаем null для project_id при проекте УСП
          getSectionMap()
        ]);
        
        if (patternRes) {
          this.treeData = this.buildTree(patternRes);
          this.expandFirstLevel();
        }
        
        if (projectRes) {
          this.projectTreeData = this.buildProjectTree(projectRes);
          this.expandFirstLevelProjects();
        }

        this.sectionMapRows = sectionMapRes || [];
      } catch (e) {
        console.error('Ошибка загрузки данных:', e);
      } finally {
        this.isLoading = false;
        this.isLoadingProjects = false;
      }
    },
    async runCheckSendReadiness() {
      if (!this.activeProjectGuid) return;

      this.isCheckingSend = true;
      this.checkSendStatusFilter = 'all';
      this.checkSendProjectTypeFilter = 'all';
      this.checkSendFilter = '';
      this.smetaSummaryItems = [];
      this.smetaSummaryFilter = '';
      this.smetaSummaryStatusFilter = 'all';
      this.smetaSummaryContractorFilter = 'all';

      try {
        const res = await checkSendReadiness(this.activeProjectGuid, null);

        this.checkSendDialog = {
          show: true,
          items: res || []
        };

        const errors = (res || []).filter(i => i.status !== 'ok').length;
        if (errors === 0) {
          Notify.create({
            type: 'positive',
            message: 'Все этажи готовы к отправке',
            position: 'top',
            timeout: 3000
          });
        }
      } catch (error) {
        console.error('Ошибка проверки отправки:', error);
        Notify.create({
          type: 'negative',
          message: error?.response?.data?.message || 'Ошибка при проверке готовности отправки',
          position: 'top',
        });
      } finally {
        this.isCheckingSend = false;
      }
    },
    async openProjectSelector() {
      this.projectSelectorDialog.show = true;
      this.projectSearchQuery = '';
      
      // Загружаем список проектов если еще не загружен
      if (this.projectGroups.length === 0) {
        await this.loadProjectGroups();
      }
    },
    async loadProjectGroups() {
      this.isLoadingProjects = true;
      
      try {
        const result = await getProjectGroups();
        this.projectGroups = result || [];
      } catch (error) {
        console.error('Ошибка загрузки списка проектов:', error);
        Notify.create({
          type: 'negative',
          message: 'Ошибка при загрузке списка проектов',
          position: 'top',
        });
      } finally {
        this.isLoadingProjects = false;
      }
    },
    async onIngestProjectGroups() {
      this.isIngestingProjects = true;
      
      try {
        await ingestProjectGroups();
        
        Notify.create({
          type: 'positive',
          message: 'Список проектов УСП успешно обновлён',
          position: 'top',
        });
        
        // Перезагружаем список проектов
        await this.loadProjectGroups();
      } catch (error) {
        console.error('Ошибка обновления списка проектов:', error);
        Notify.create({
          type: 'negative',
          message: error?.response?.data?.message || 'Ошибка при обновлении списка проектов',
          position: 'top',
        });
      } finally {
        this.isIngestingProjects = false;
      }
    },
    selectProject(project) {
      this.projectGuid = project.project_guid;
      this.selectedProjectName = project.project_name;
      this.selectedProjectUrl = project.url || '';
      this.projectSelectorDialog.show = false;
      
      // Автоматически загружаем данные
      this.loadData();
    },
    clearUspProject() {
      const wasUspProjectSelected = !!this.projectGuid;
      
      this.projectGuid = '';
      this.selectedProjectName = '';
      this.selectedProjectUrl = '';
      
      // Очищаем деревья (взаимоисключающие проекты)
      this.treeData = [];
      this.projectTreeData = [];
      this.expanded = [];
      this.expandedProjects = [];
      this.clearEntranceFilter();
      
      if (wasUspProjectSelected) {
        Notify.create({
          type: 'info',
          message: 'Выбор проекта УСП сброшен',
          position: 'top',
          timeout: 2000
        });
      }
    },
    async refreshProjectPattern() {
      if (!this.activeProjectGuid) return;
      
      this.isRefreshingPattern = true;
      
      try {
        const result = await ingestProjectPattern(this.activeProjectGuid, this.selectedProjectUrl);
        
        Notify.create({
          type: 'positive',
          message: 'Структура проекта УСП успешно обновлена',
          position: 'top',
          timeout: 3000
        });
        
        // Реактивно обновляем правое дерево (УСП)
        await this.loadDataSilently();
      } catch (error) {
        console.error('Ошибка обновления структуры:', error);
        Notify.create({
          type: 'negative',
          message: error?.response?.data?.message || 'Ошибка при обновлении структуры проекта УСП',
          position: 'top',
        });
      } finally {
        this.isRefreshingPattern = false;
      }
    },
    expandAll() {
      const allIds = [];
      const collectIds = (nodes) => {
        nodes.forEach(node => {
          allIds.push(node.id);
          if (node.children && node.children.length > 0) {
            collectIds(node.children);
          }
        });
      };
      collectIds(this.treeData);
      this.expanded = allIds;
    },
    collapseAll() {
      this.expanded = [];
    },
    expandFirstLevel() {
      const firstLevelIds = this.treeData.map(node => node.id);
      this.expanded = firstLevelIds;
    },
    toggleNode(nodeId) {
      const index = this.expanded.indexOf(nodeId);
      if (index > -1) {
        this.expanded.splice(index, 1);
      } else {
        this.expanded.push(nodeId);
      }
    },
    expandAllProjects() {
      const allIds = [];
      const collectIds = (nodes) => {
        nodes.forEach(node => {
          allIds.push(node.id);
          if (node.children && node.children.length > 0) {
            collectIds(node.children);
          }
        });
      };
      collectIds(this.projectTreeData);
      this.expandedProjects = allIds;
    },
    collapseAllProjects() {
      this.expandedProjects = [];
    },
    expandFirstLevelProjects() {
      const firstLevelIds = this.projectTreeData.map(node => node.id);
      this.expandedProjects = firstLevelIds;
    },
    toggleProjectNode(nodeId) {
      const index = this.expandedProjects.indexOf(nodeId);
      if (index > -1) {
        this.expandedProjects.splice(index, 1);
      } else {
        this.expandedProjects.push(nodeId);
      }
    },
    buildTree(data) {
      if (!Array.isArray(data) || data.length === 0) {
        return [];
      }

      // Если данные уже в виде дерева (есть поле children), просто форматируем
      if (data[0] && data[0].children !== undefined) {
        return this.formatTreeNodes(data);
      }

      // Строим дерево из плоских данных
      // Используем guid как ключ узла и parent_guid для связи
      const nodeMap = new Map();
      const roots = [];

      // Создаем карту всех узлов по guid
      data.forEach((item) => {
        const node = {
          id: item.guid || item.project_pattern_id,
          label: item.name || 'Без названия',
          guid: item.guid,
          parent_guid: item.parent_guid,
          its_work: item.its_work,
          project_type: item.project_type,
          guid_wbs: item.guid_wbs,
          children: [],
          ...item,
        };
        nodeMap.set(node.guid, node);
      });

      // Строим дерево по parent_guid
      data.forEach((item) => {
        const node = nodeMap.get(item.guid);
        
        if (item.parent_guid && nodeMap.has(item.parent_guid)) {
          // Есть родитель - добавляем к нему
          const parent = nodeMap.get(item.parent_guid);
          parent.children.push(node);
        } else {
          // Нет родителя - это корневой узел
          roots.push(node);
        }
      });

      return this.formatTreeNodes(roots);
    },
    formatTreeNodes(nodes, depth = 0) {
      return nodes.map(node => {
        const formatted = {
          ...node,
          isBold: node.its_work === true, // Работы выделяем жирным
          depth: depth,
          // Проблема: 1) есть дети, но нет работы ИЛИ 2) у братьев есть работа, а у меня нет
          missingWork: (node.has_children === true && node.has_work_child === false) || 
                       (node.siblings_have_work === true && node.has_work_child === false),
        };

        // Добавляем детей, если они есть
        if (node.children && node.children.length > 0) {
          formatted.children = this.formatTreeNodes(node.children, depth + 1);
          // Считаем количество проблем в поддереве
          formatted.missingWorkCount = this.countMissingWork(formatted);
        }

        return formatted;
      });
    },
    countMissingWork(node) {
      // Считаем количество узлов с missingWork в поддереве
      let count = node.missingWork ? 1 : 0;
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => {
          count += this.countMissingWork(child);
        });
      }
      
      return count;
    },
    hasWorkInSubtree(node) {
      // Если сам узел - работа, возвращаем true
      if (node.its_work === true) {
        return true;
      }
      
      // Если есть дети - проверяем рекурсивно
      if (node.children && node.children.length > 0) {
        return node.children.some(child => this.hasWorkInSubtree(child));
      }
      
      // Нет детей и сам не работа
      return false;
    },
    countNoWorkBranches(node) {
      // Считаем количество прямых детей без работ
      if (!node.children || node.children.length === 0) {
        return 0;
      }
      return node.children.filter(child => child.hasNoWork && child.children && child.children.length > 0).length;
    },
    countWithWorkBranches(node) {
      // Рекурсивно считаем количество веток с работами (its_work = true)
      let count = 0;
      
      const countRecursive = (n) => {
        if (n.its_work === true) {
          count++;
        }
        if (n.children && n.children.length > 0) {
          n.children.forEach(child => countRecursive(child));
        }
      };
      
      if (node.children && node.children.length > 0) {
        node.children.forEach(child => countRecursive(child));
      }
      
      return count;
    },
    collectNoWorkBranches(node, path = []) {
      // Собираем список веток без работ с их путями
      const result = [];
      
      if (!node.children || node.children.length === 0) {
        return result;
      }
      
      node.children.forEach(child => {
        const childPath = [...path, child.label];
        
        if (child.hasNoWork && child.children && child.children.length > 0) {
          result.push({
            label: child.label,
            guid: child.guid,
            path: childPath.join(' → ')
          });
        }
        
        // Рекурсивно собираем из детей
        if (child.children && child.children.length > 0) {
          result.push(...this.collectNoWorkBranches(child, childPath));
        }
      });
      
      return result;
    },
    buildProjectTree(data) {
      if (!Array.isArray(data) || data.length === 0) {
        return [];
      }

      // Группируем: Resident -> Тип проекта -> Блок -> Этажи
      const residentsMap = new Map();

      data.forEach((item) => {
        const residentKey = item.resident_name;
        
        // 1. Создаем Resident если его нет
        if (!residentsMap.has(residentKey)) {
          residentsMap.set(residentKey, {
            id: `resident_${residentKey}`,
            label: item.resident_name,
            resident_id: item.resident_id,
            children: [],
            typesMap: new Map(),
          });
        }

        const resident = residentsMap.get(residentKey);
        
        // Обновляем resident_id если он еще не установлен
        if (!resident.resident_id && item.resident_id) {
          resident.resident_id = item.resident_id;
        }
        
        const typeKey = `${residentKey}_type_${item.project_type_id}`;

        // 2. Создаем тип проекта если его нет
        if (!resident.typesMap.has(typeKey)) {
          const projectType = {
            id: typeKey,
            label: item.project_type_name,
            project_type_id: item.project_type_id,
            project_type_code: item.project_type_code,
            children: [],
            blocksMap: new Map(),
          };
          resident.typesMap.set(typeKey, projectType);
          resident.children.push(projectType);
        }

        const projectType = resident.typesMap.get(typeKey);
        // Ключ включает project_entrance_id, т.к. один блок может иметь несколько подъездов —
        // каждый подъезд является отдельным узлом дерева со своим project_entrance_id и usp_block_guid
        const blockKey = `entrance_${item.project_entrance_id}`;

        // 3. Создаем блок если его нет
        if (!projectType.blocksMap.has(blockKey)) {
          const block = {
            id: blockKey,
            label: `Блок ${item.block_name} (Подъезд ${item.entrance})`,
            block_name: item.block_name,
            entrance: item.entrance,
            project_id: item.project_id,
            project_entrance_id: item.project_entrance_id,
            usp_block_guid: item.usp_block_guid || null,
            // Поля отправки в УСП
            is_send_usp_contract: item.is_send_usp_contract,
            usp_contract_result: item.usp_contract_result,
            usp_contract_error: item.usp_contract_error,
            usp_contract_send_datetime: item.usp_contract_send_datetime,
            is_send_usp_estimate: item.is_send_usp_estimate,
            usp_estimate_result: item.usp_estimate_result,
            usp_estimate_error: item.usp_estimate_error,
            usp_estimate_send_datetime: item.usp_estimate_send_datetime,
            children: [],
            floorsMap: new Map(),
          };
          projectType.blocksMap.set(blockKey, block);
          projectType.children.push(block);
        }

        const block = projectType.blocksMap.get(blockKey);
        
        const floorKey = `floor_${item.floor_map_id}`;

        // 4. Добавляем этаж если его нет (избегаем дублей)
        if (!block.floorsMap.has(floorKey)) {
          const floor = {
            id: floorKey,
            label: `Этаж ${item.level_num}`,
            level_num: item.level_num,
            floor_map_id: item.floor_map_id,
            project_id: item.project_id,
            project_entrance_id: item.project_entrance_id,
            usp_level_guid: item.usp_level_guid,
            // Статус оперфакта
            has_operfact: item.has_operfact,
            has_operfact_pending: item.has_operfact_pending,
            has_operfact_error: item.has_operfact_error,
          };
          block.floorsMap.set(floorKey, floor);
          block.children.push(floor);
        } else {
          // Обновляем usp_level_guid если он есть
          const existingFloor = block.floorsMap.get(floorKey);
          if (item.usp_level_guid && !existingFloor.usp_level_guid) {
            existingFloor.usp_level_guid = item.usp_level_guid;
          }
        }
      });

      // Сортируем и агрегируем
      residentsMap.forEach(resident => {
        resident.children.forEach(projectType => {
          projectType.children.forEach(block => {
            // Сортируем этажи
            block.children.sort((a, b) => a.level_num - b.level_num);
            // Агрегируем статус оперфакта на блок
            block.has_operfact = block.children.some(f => f.has_operfact);
            block.has_operfact_pending = block.children.some(f => f.has_operfact_pending);
            block.has_operfact_error = block.children.some(f => f.has_operfact_error);
            delete block.floorsMap;
          });
          // Сортируем блоки по номеру
          projectType.children.sort((a, b) => {
            const aNum = parseInt(a.block_name) || 0;
            const bNum = parseInt(b.block_name) || 0;
            return aNum - bNum;
          });
          delete projectType.blocksMap;
        });
        // Сортируем типы проектов по названию
        resident.children.sort((a, b) => a.label.localeCompare(b.label));
        delete resident.typesMap;
      });

      // Сортируем residents
      const result = Array.from(residentsMap.values());
      result.sort((a, b) => a.label.localeCompare(b.label));

      return result;
    },
    isDraggableEntrance(node) {
      // Можно перетаскивать подъезды (блоки) и этажи из левого дерева Master
      // Подъезд имеет project_entrance_id, этаж имеет floor_map_id
      return !!node.project_entrance_id || !!node.floor_map_id;
    },
    canDragNode(node) {
      // Сначала проверяем этаж (у этажа есть и floor_map_id и project_entrance_id)
      // Этажи можно тащить только если выбран подъезд для фильтрации
      // И этаж принадлежит выбранному подъезду
      if (node.floor_map_id) {
        if (!this.selectedEntranceForFloors) {
          return false;
        }
        // Проверяем, принадлежит ли этаж выбранному подъезду
        return this.isFloorOfEntrance(node, this.selectedEntranceForFloors);
      }
      // Подъезды (блоки) можно всегда тащить
      if (node.project_entrance_id) {
        return true;
      }
      return false;
    },
    isFloorOfEntrance(floorNode, entranceNode) {
      // Проверяем, принадлежит ли этаж указанному подъезду
      // Ищем этаж среди детей подъезда
      if (!entranceNode.children) return false;
      
      return entranceNode.children.some(child => 
        child.floor_map_id === floorNode.floor_map_id
      );
    },
    toggleEntranceFilter(node) {
      // Если этот подъезд уже выбран - сбрасываем фильтр
      if (this.selectedEntranceForFloors?.project_entrance_id === node.project_entrance_id) {
        this.clearEntranceFilter();
        return;
      }
      
      // Проверяем, что подъезд связан с УСП
      if (!node.usp_block_guid) {
        Notify.create({
          type: 'warning',
          message: 'Подъезд не связан с блоком УСП',
          position: 'top',
        });
        return;
      }
      
      // Устанавливаем выбранный подъезд
      this.selectedEntranceForFloors = node;
      
      // Фильтруем правое дерево - показываем только связанный блок УСП и его детей
      this.filterTreeByBlock(node.usp_block_guid);
      
      Notify.create({
        type: 'info',
        message: `Фильтр установлен: ${node.label}`,
        position: 'top',
      });
    },
    clearEntranceFilter() {
      this.selectedEntranceForFloors = null;
      this.filteredTreeData = [];
    },
    filterTreeByBlock(uspBlockGuid) {
      // Рекурсивно находим узел с нужным guid и возвращаем его с детьми
      const findNodeByGuid = (nodes, guid) => {
        for (const node of nodes) {
          if (node.guid === guid) {
            return node;
          }
          if (node.children && node.children.length > 0) {
            const found = findNodeByGuid(node.children, guid);
            if (found) return found;
          }
        }
        return null;
      };
      
      const blockNode = findNodeByGuid(this.treeData, uspBlockGuid);
      
      if (blockNode) {
        this.filteredTreeData = [blockNode];
        // Автоматически разворачиваем отфильтрованный узел
        this.$nextTick(() => {
          if (!this.expanded.includes(blockNode.id)) {
            this.expanded.push(blockNode.id);
          }
        });
      } else {
        this.filteredTreeData = [];
      }
    },
    filterUnlinkedNodes(nodes, side) {
      // Рекурсивно фильтруем несвязанные узлы
      const filterRecursive = (nodeList) => {
        return nodeList.reduce((filtered, node) => {
          const nodeCopy = { ...node };
          
          // Для левого дерева - проверяем usp_block_guid и usp_level_guid
          if (side === 'left') {
            const isEntranceUnlinked = node.project_entrance_id && !node.floor_map_id && !node.usp_block_guid;
            const isFloorUnlinked = node.floor_map_id && !node.usp_level_guid;
            const isUnlinked = isEntranceUnlinked || isFloorUnlinked;
            
            // Фильтруем детей
            if (node.children && node.children.length > 0) {
              nodeCopy.children = filterRecursive(node.children);
            }
            
            // Добавляем узел если он несвязан или у него есть несвязанные дети
            if (isUnlinked || (nodeCopy.children && nodeCopy.children.length > 0)) {
              filtered.push(nodeCopy);
            } else if (!node.project_entrance_id && !node.floor_map_id) {
              // Это родительский узел - добавляем если есть дети
              if (nodeCopy.children && nodeCopy.children.length > 0) {
                filtered.push(nodeCopy);
              }
            }
          }
          
          // Для правого дерева - показываем узлы без привязанных элементов
          if (side === 'right') {
            const hasLinkedItems = this.hasLinkedEntrances(node);
            
            // Фильтруем детей
            if (node.children && node.children.length > 0) {
              nodeCopy.children = filterRecursive(node.children);
            }
            
            // Добавляем узел если у него нет связей или есть несвязанные дети
            if (!hasLinkedItems || (nodeCopy.children && nodeCopy.children.length > 0)) {
              filtered.push(nodeCopy);
            }
          }
          
          return filtered;
        }, []);
      };
      
      return filterRecursive(nodes);
    },
    getFloorStats(entranceNode) {
      // Подсчитываем статистику связанных этажей
      if (!entranceNode.children) return '';
      
      const totalFloors = entranceNode.children.length;
      const linkedFloors = entranceNode.children.filter(floor => floor.usp_level_guid).length;
      
      return `этажи: ${linkedFloors} / ${totalFloors}`;
    },
    getFloorStatsColor(entranceNode) {
      // Цвет бейджа в зависимости от статистики
      if (!entranceNode.children) return 'grey';
      
      const totalFloors = entranceNode.children.length;
      const linkedFloors = entranceNode.children.filter(floor => floor.usp_level_guid).length;
      
      if (linkedFloors === 0) return 'grey';
      if (linkedFloors === totalFloors) return 'green';
      return 'orange';
    },
    formatDateTime(dateTime) {
      if (!dateTime) return '';
      const date = new Date(dateTime);
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    },
    async openEstimateDialog(node) {
      this.uspDetailDialog.type = 'estimate';
      this.uspDetailDialog.title = `Смета: ${node.label}`;
      this.uspDetailDialog.node = node;
      this.uspDetailDialog.uspJson = null;
      this.uspDetailDialog.uspJsonLoading = true;
      this.uspDetailDialog.show = true;
      await this.loadUspJsonForNode(node);
    },
    async openContractDialog(node) {
      this.uspDetailDialog.type = 'contract';
      this.uspDetailDialog.title = `Договор: ${node.label}`;
      this.uspDetailDialog.node = node;
      this.uspDetailDialog.uspJson = null;
      this.uspDetailDialog.uspJsonLoading = true;
      this.uspDetailDialog.show = true;
      await this.loadUspJsonForNode(node);
    },
    async loadUspJsonForNode(node) {
      if (!node.project_id) {
        this.uspDetailDialog.uspJsonLoading = false;
        return;
      }
      try {
        const res = await getProjectUspJson(node.project_id);
        if (res && res.length > 0) {
          this.uspDetailDialog.uspJson = res[0];
        }
      } catch (e) {
        console.error('Failed to load USP JSON:', e);
      } finally {
        this.uspDetailDialog.uspJsonLoading = false;
      }
    },
    async openOperfactDialog(node) {
      let title = 'Оперфакт';
      let floor_map_id = null;
      let project_entrance_id = null;

      if (node.floor_map_id) {
        floor_map_id = node.floor_map_id;
        title = `Оперфакт — Этаж ${node.level_num}`;
      } else if (node.project_entrance_id) {
        project_entrance_id = node.project_entrance_id;
        title = `Оперфакт — ${node.label}`;
      }

      this.operfactDialog = {
        show: true,
        loading: true,
        title: title,
        items: []
      };

      try {
        const res = await getUspFactForOperfact(floor_map_id, project_entrance_id);
        this.operfactDialog.items = res || [];
      } catch (error) {
        console.error('Ошибка загрузки данных оперфакта:', error);
        Notify.create({
          type: 'negative',
          message: 'Ошибка при загрузке данных оперфакта',
          position: 'top'
        });
      } finally {
        this.operfactDialog.loading = false;
      }
    },
    async loadFactMaterials(row) {
      this.materialsDialog = {
        show: true,
        loading: true,
        items: [],
        title: `Материалы — факт #${row.usp_fact_id}`
      };
      try {
        const res = await getUspFactMaterialByFact(row.usp_fact_id);
        this.materialsDialog.items = res || [];
      } catch (e) {
        console.error('Ошибка загрузки материалов:', e);
        Notify.create({ type: 'negative', message: 'Ошибка загрузки материалов', position: 'top' });
      } finally {
        this.materialsDialog.loading = false;
      }
    },
    showJson(jsonStr, title) {
      try {
        const parsed = JSON.parse(jsonStr);
        this.jsonDialog = {
          show: true,
          title: title,
          content: JSON.stringify(parsed, null, 2)
        };
      } catch (e) {
        this.jsonDialog = {
          show: true,
          title: title,
          content: jsonStr
        };
      }
    },
    copyJson() {
      navigator.clipboard.writeText(this.jsonDialog.content).then(() => {
        Notify.create({
          type: 'positive',
          message: 'Скопировано в буфер обмена',
          position: 'top',
          timeout: 1500
        });
      });
    },
    isDropTarget(node) {
      if (!this.draggedNode) return false;
      // Можно бросить на любой узел УСП (правое дерево)
      return node.guid && !node.project_entrance_id && !node.floor_map_id;
    },
    isLinked(node) {
      // Проверяем, есть ли связь у подъезда или этажа
      if (node.project_entrance_id && node.usp_block_guid) {
        return true;
      }
      if (node.floor_map_id && node.usp_level_guid) {
        return true;
      }
      return false;
    },
    getLinkedNodeName(node) {
      const guid = node.usp_block_guid || node.usp_level_guid;
      if (!guid) return null;
      return this.uspGuidToLabelMap.get(guid) || null;
    },
    getLinkedItemsSummary(node) {
      if (!node.guid) {
        return { blockCount: 0, floorCount: 0, totalCount: 0, items: [] };
      }
      const cached = this.linkedSummaryByGuid.get(node.guid);
      if (!cached) {
        return { blockCount: 0, floorCount: 0, totalCount: 0, items: [] };
      }
      return {
        ...cached,
        items: cached.items.map(item => ({
          ...item,
          path: this.buildPathToProjectNode(item.node)
        }))
      };
    },
    hasLinkedEntrances(node) {
      if (!node.guid) return false;
      const cached = this.linkedSummaryByGuid.get(node.guid);
      return cached ? (cached.blockCount > 0 || cached.floorCount > 0) : false;
    },
    getLinkedEntrancesCount(node) {
      if (!node.guid) return 0;
      const cached = this.linkedSummaryByGuid.get(node.guid);
      return cached ? cached.totalCount : 0;
    },
    getLinkedNodesType(node) {
      // Определяем тип связанных узлов (подъезды или этажи)
      const summary = this.getLinkedItemsSummary(node);

      if (summary.blockCount > 0 && summary.floorCount > 0) {
        return 'mixed'; // Смешанный тип
      } else if (summary.blockCount > 0) {
        return 'entrance'; // Только подъезды
      } else if (summary.floorCount > 0) {
        return 'floor'; // Только этажи
      }
      return null;
    },
    getLinkedNodesBadgeLabel(node) {
      if (!node.guid) return '';
      const cached = this.linkedSummaryByGuid.get(node.guid);
      if (!cached || cached.totalCount === 0) return '';

      if (cached.blockCount > 0 && cached.floorCount > 0) {
        return `блоки ${cached.blockCount}, этажи ${cached.floorCount}`;
      }

      if (cached.blockCount > 0) {
        return `блоки ${cached.blockCount}`;
      }

      return `этажи ${cached.floorCount}`;
    },
    getLinkedItems(node) {
      return this.getLinkedItemsSummary(node).items;
    },
    showLinkedItems(node) {
      // Показываем модальное окно со списком привязанных элементов
      this.linkedItemsDialog = {
        show: true,
        nodeName: node.label || 'Узел',
        items: this.getLinkedItems(node)
      };
    },
    showNoWorkDialog(node) {
      // Показываем модальное окно со списком веток без работ
      this.noWorkDialog = {
        show: true,
        nodeName: node.label || 'Узел',
        items: node.noWorkBranches || []
      };
    },
    showMissingWorkDialog(node) {
      // Собираем проблемные узлы из поддерева и показываем диалог
      const items = this.collectMissingWorkNodes(node, []);
      const breadcrumbs = this.buildBreadcrumbs(node.guid);
      this.noWorkDialog = {
        show: true,
        nodeName: node.label || 'Узел',
        breadcrumbs: breadcrumbs,
        items: items
      };
    },
    buildBreadcrumbs(targetGuid) {
      // Строим путь от корня до узла
      const findPath = (nodes, guid, path = []) => {
        for (const node of nodes) {
          const currentPath = [...path, node.label];
          if (node.guid === guid) {
            return currentPath;
          }
          if (node.children && node.children.length > 0) {
            const found = findPath(node.children, guid, currentPath);
            if (found) return found;
          }
        }
        return null;
      };
      return findPath(this.treeData, targetGuid) || [];
    },
    collectMissingWorkNodes(node, path = [], inProblemBranch = false) {
      const result = [];
      const currentPath = [...path, node.label];
      const hasChildren = node.children && node.children.length > 0;
      
      // Если это работа (its_work = true) - пропускаем
      if (node.its_work === true) {
        return result;
      }
      
      // Если у узла есть работа внутри - он НЕ проблемный, не добавляем его
      // Но продолжаем искать проблемы в его детях
      if (node.has_work_child === true) {
        if (hasChildren) {
          node.children.forEach(child => {
            result.push(...this.collectMissingWorkNodes(child, currentPath, false));
          });
        }
        return result;
      }
      
      // Входим в проблемную ветку если текущий узел имеет missingWork
      const isProblem = inProblemBranch || node.missingWork;
      
      if (isProblem) {
        // Мы в проблемной ветке - ищем конечную точку
        // Конец если: нет детей ИЛИ все дети - работы
        const isEndPoint = !hasChildren || (hasChildren && node.children.every(c => c.its_work === true));
        
        if (isEndPoint) {
          result.push({
            label: node.label,
            guid: node.guid,
            fullPath: currentPath.join(' → '),
            pathWithoutLast: path.join(' → ')
          });
        }
      }
      
      // Идём глубже по детям
      if (hasChildren) {
        node.children.forEach(child => {
          result.push(...this.collectMissingWorkNodes(child, currentPath, isProblem));
        });
      }
      
      return result;
    },
    async removeLinkedItem(item) {
      // Удаляем связь из модального окна
      await this.deleteLinkHandler(item.node);
      // Закрываем диалог - данные обновятся через loadDataSilently
      this.linkedItemsDialog.show = false;
    },
    async performSingleLink(draggedNode, targetNode) {
      const draggedLabel = draggedNode.label || 'Узел';
      const targetLabel = targetNode.label || 'Узел';
      const draggedEntranceId = draggedNode.project_entrance_id;
      const draggedFloorId = draggedNode.floor_map_id;
      const targetGuid = targetNode.guid;
      
      try {
        // Сначала проверяем этаж (у этажа есть и floor_map_id и project_entrance_id)
        if (draggedFloorId && targetGuid) {
          await updateFloorLink(draggedFloorId, targetGuid);
          
          Notify.create({
            type: 'positive',
            message: `Связь создана: ${draggedLabel} ↔ ${targetLabel}`,
            position: 'top',
          });
          
          this.selectedLeftNode = null;
          this.selectedRightNode = null;
          await this.loadDataSilently();
        }
        // Иначе это блок (только project_entrance_id, без floor_map_id)
        else if (draggedEntranceId && targetGuid) {
          await updateEntranceLink(draggedEntranceId, targetGuid);
          
          Notify.create({
            type: 'positive',
            message: `Связь создана: ${draggedLabel} ↔ ${targetLabel}`,
            position: 'top',
          });
          
          this.selectedLeftNode = null;
          this.selectedRightNode = null;
          await this.loadDataSilently();
        }
      } catch (error) {
        console.error('Ошибка создания связи:', error);
        Notify.create({
          type: 'negative',
          message: 'Ошибка при создании связи',
          position: 'top',
        });
      }
    },
    async loadDataSilently() {
      if (!this.activeProjectGuid) return;
      this.isSilentLoading = true;
      try {
        // Сохраняем ID выбранного подъезда
        const selectedEntranceId = this.selectedEntranceForFloors?.project_entrance_id;
        
        const [patternRes, projectRes, sectionMapRes] = await Promise.all([
          getProjectPattern(this.activeProjectGuid),
          getProjectByGuid(this.activeProjectGuid, null),
          getSectionMap()
        ]);
        
        if (patternRes) {
          this.treeData = this.buildTree(patternRes);
        }
        
        if (projectRes) {
          this.projectTreeData = this.buildProjectTree(projectRes);
        }

        this.sectionMapRows = sectionMapRes || [];
        
        // Восстанавливаем фильтр если был выбран подъезд
        if (selectedEntranceId) {
          const entranceNode = this.findEntranceNodeById(this.projectTreeData, selectedEntranceId);
          if (entranceNode && entranceNode.usp_block_guid) {
            this.selectedEntranceForFloors = entranceNode;
            this.filterTreeByBlock(entranceNode.usp_block_guid);
          } else {
            // Если подъезд не найден или отвязан - сбрасываем фильтр
            this.clearEntranceFilter();
          }
        }
      } catch (e) {
        console.error('Ошибка тихой загрузки данных:', e);
      } finally {
        this.isSilentLoading = false;
      }
    },
    findEntranceNodeById(nodes, entranceId) {
      // Рекурсивно ищем узел подъезда (блока) по project_entrance_id
      // Важно: исключаем этажи (у них тоже есть project_entrance_id от родительского блока)
      for (const node of nodes) {
        if (node.project_entrance_id === entranceId && !node.floor_map_id) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = this.findEntranceNodeById(node.children, entranceId);
          if (found) return found;
        }
      }
      return null;
    },
    removeLink(node) {
      // Проверяем, есть ли связь у подъезда или этажа
      const hasEntranceLink = node.project_entrance_id && node.usp_block_guid;
      const hasFloorLink = node.floor_map_id && node.usp_level_guid;
      
      if (!hasEntranceLink && !hasFloorLink) {
        return;
      }
      
      const nodeLabel = node.label || 'Узел';
      
      // Показываем диалог подтверждения
      this.confirmDialog = {
        show: true,
        title: 'Удалить связь',
        message: `Вы уверены, что хотите удалить связь для "${nodeLabel}"?`,
        onConfirm: async () => {
          await this.deleteLinkHandler(node);
        }
      };
    },
    async deleteLinkHandler(node) {
      const nodeLabel = node.label || 'Узел';
      
      try {
        // Удаляем связь подъезда
        if (node.project_entrance_id && node.usp_block_guid) {
          await updateEntranceLink(node.project_entrance_id, null);
        }
        
        // Удаляем связь этажа
        if (node.floor_map_id && node.usp_level_guid) {
          await updateFloorLink(node.floor_map_id, null);
        }
        
        Notify.create({
          type: 'positive',
          message: `Связь удалена для: ${nodeLabel}`,
          position: 'top',
        });
        
        this.selectedLeftNode = null;
        this.selectedRightNode = null;
        await this.loadDataSilently();
      } catch (error) {
        console.error('Ошибка удаления связи:', error);
        Notify.create({
          type: 'negative',
          message: 'Ошибка при удалении связи',
          position: 'top',
        });
      }
    },
    async onConfirmDelete() {
      this.confirmDialog.show = false;
      if (this.confirmDialog.onConfirm) {
        await this.confirmDialog.onConfirm();
      }
    },
    showLinkPath(node) {
      // Показываем путь связи для подъезда или этажа
      const guid = node.usp_block_guid || node.usp_level_guid;
      if (!guid) return;
      
      const path = this.buildPathToNode(guid);
      const targetNode = this.findNodeByGuid(this.treeData, guid);
      
      this.linkPathDialog = {
        show: true,
        sourceName: node.label || 'Узел',
        targetName: targetNode ? targetNode.label : 'Узел УСП',
        path: path
      };
    },
    buildPathToNode(targetGuid) {
      // Строим путь от корня до узла с указанным guid в дереве УСП
      const path = [];
      
      const findPath = (nodes, guid, currentPath) => {
        for (const node of nodes) {
          const newPath = [...currentPath, node];
          
          if (node.guid === guid) {
            return newPath;
          }
          
          if (node.children && node.children.length > 0) {
            const result = findPath(node.children, guid, newPath);
            if (result) {
              return result;
            }
          }
        }
        return null;
      };
      
      const result = findPath(this.treeData, targetGuid, []);
      return result || [];
    },
    buildPathToProjectNode(targetNode) {
      // Строим путь от корня до узла в дереве проектов (левое дерево)
      if (!targetNode) return [];
      
      const findPath = (nodes, target, currentPath) => {
        for (const node of nodes) {
          const newPath = [...currentPath, node];
          
          // Сравниваем по id узла
          if (node.id === target.id) {
            return newPath;
          }
          
          if (node.children && node.children.length > 0) {
            const result = findPath(node.children, target, newPath);
            if (result) {
              return result;
            }
          }
        }
        return null;
      };
      
      const result = findPath(this.projectTreeData, targetNode, []);
      return result || [];
    },
    findNodeByGuid(nodes, guid) {
      // Находим узел по guid в дереве
      for (const node of nodes) {
        if (node.guid === guid) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = this.findNodeByGuid(node.children, guid);
          if (found) return found;
        }
      }
      return null;
    },
  },
  mounted() {},
};
</script>

<style scoped lang="scss">
.project-pattern-structure {
  padding: 20px;
  height: 100%;

  &__header {
    margin-bottom: 20px;
    
    .header-controls {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .header-label {
        font-weight: 500;
        color: #333;
      }
    }
  }

  &__content {
    .panel-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      padding: 0 12px;

      .panel-header {
        padding: 12px 0;
        border-bottom: 2px solid #e0e0e0;
        margin-bottom: 12px;

        .panel-title {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
        }
      }
    }

    .tree-container {
      flex: 1;
      overflow-y: auto;
      padding-right: 4px;

      .tree-controls {
        display: flex;
        gap: 8px;
        margin-bottom: 12px;
        padding-bottom: 12px;
        border-bottom: 1px solid #e0e0e0;
      }

      .tree-node {
        cursor: pointer;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f5f5f5;
        }

        .expand-btn {
          color: #1976d2;
        }

        &.tree-node--selected-left {
          background-color: #e0f2fe;
        }

        &.tree-node--selected-right {
          background-color: #ede9fe;
        }

        .project-type-map-info {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          margin-top: 4px;
        }

        .project-type-map-badge {
          font-size: 11px;
          font-weight: 600;
        }

        .project-type-map-text {
          font-size: 12px;
          color: #5f6368;
        }

        .project-type-map-node {
          font-size: 11px;
          color: #9aa0a6;
        }

        .project-type-map-tooltip-line {
          white-space: nowrap;
          font-size: 12px;
        }
      }
    }

    .empty-state {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      text-align: center;
      color: #666;

      p {
        margin-top: 12px;
        font-size: 14px;
      }
    }
  }
}

// Стили для кликабельного бейджа
.linked-badge {
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

// Стили для элементов в списке привязанных
.linked-item {
  transition: background-color 0.2s ease;
  
  &:hover {
    background-color: #f5f5f5;
  }
}

.linked-item-card {
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.check-send-scroll {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-height: calc(100vh - 170px);
  overflow: auto;
  padding-right: 4px;
}

:deep(.check-send-group-header) {
  position: sticky;
  top: 0;
  z-index: 5;
  background: linear-gradient(180deg, #f8fafc 0%, #f3f4f6 100%);
  border-bottom: 1px solid #e0e0e0;
  box-shadow: inset 0 -1px 0 rgba(15, 23, 42, 0.05);
}

:deep(.check-send-group-header .q-item) {
  align-items: flex-start;
}

.check-send-summary-panel {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-right: 16px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

.check-send-summary-badge {
  padding: 7px 10px;
  font-weight: 600;
}

.check-send-tabs {
  padding: 8px 16px 0;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
}

:deep(.check-send-tabs .q-tabs__content) {
  gap: 4px;
}

:deep(.check-send-tab) {
  min-width: auto;
  padding: 0;
  margin: 0;
}

:deep(.check-send-tab .q-focus-helper) {
  opacity: 0 !important;
  background: transparent !important;
}

:deep(.check-send-tab .q-tab__content) {
  padding: 0;
}

.check-send-tab-content {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 12px 12px 0 0;
  background: #f8fafc;
  font-weight: 600;
  color: #475569;
  transition: all 0.2s ease;
}

:deep(.check-send-tab:not(.q-tab--active):hover .check-send-tab-content) {
  background: #f1f5f9;
  border-color: #dbe3ee;
}

.check-send-tab-badge {
  min-width: 22px;
  justify-content: center;
}

:deep(.check-send-tab.q-tab--active .check-send-tab-content) {
  background: #eef4ff;
  border-color: #bfd3ff;
  color: #1d4ed8;
  box-shadow: inset 0 -2px 0 #2563eb;
}

.check-send-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.check-send-status-filters {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  padding: 6px 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  background: #f8fafc;
}

.check-send-status-btn {
  min-height: 30px;
  padding: 0 4px;
  font-weight: 600;
  font-size: 12px;
}

.check-send-project-type-select {
  min-width: 280px;
}

.check-send-search-input {
  min-width: 240px;
}

:deep(.check-send-project-type-select .q-field__control),
:deep(.check-send-search-input .q-field__control) {
  min-height: 34px;
  height: 34px;
}

:deep(.check-send-project-type-select .q-field__native),
:deep(.check-send-search-input .q-field__native),
:deep(.check-send-project-type-select .q-field__input),
:deep(.check-send-search-input .q-field__input) {
  font-size: 12px;
}

:deep(.check-send-project-type-select .q-field__append),
:deep(.check-send-search-input .q-field__append) {
  height: 34px;
}

.check-send-project-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.check-send-project-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2937;
  line-height: 1.25;
}

.check-send-project-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.check-send-project-id {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.3px;
}

.check-send-project-type-badge,
.check-send-project-count-badge,
.check-send-project-main-status {
  font-size: 12px;
  font-weight: 600;
}

.check-send-project-type-badge {
  padding: 6px 10px;
  border: 1px solid rgba(79, 70, 229, 0.14);
}

.check-send-project-main-status {
  padding: 6px 10px;
  box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
}

.check-send-project-summary {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
  max-width: 420px;
}

.check-send-project-summary-badge {
  padding: 4px 8px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.1px;
}

:deep(.check-send-table thead tr th) {
  background: #fff;
  box-shadow: inset 0 -1px 0 rgba(15, 23, 42, 0.06);
}

@media (max-width: 1180px) {

  :deep(.check-send-group-header .q-item) {
    flex-wrap: wrap;
  }

  :deep(.check-send-group-header .q-item__section--main) {
    min-width: 0;
    width: 100%;
  }

  .check-send-project-side {
    width: 100%;
    padding-top: 8px;
  }

  .check-send-project-summary {
    justify-content: flex-start;
    max-width: none;
  }
}

.smeta-summary-side {
  display: flex;
  align-items: center;
  gap: 12px;
}

.smeta-summary-side-item {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1px;
}

.smeta-summary-side-label {
  font-size: 10px;
  color: #888;
  font-weight: 500;
}

.smeta-summary-side-value {
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
}

.smeta-summary-side-divider {
  width: 1px;
  height: 28px;
  background: #ddd;
}
</style>
